import { InlineDraftControl } from "@/components/admin/support/InlineDraftControl";
import { isOpenableUrl } from "@/lib/open-document-tab";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { resolveStoredFileUrl, storageRefFor } from "@/lib/storage-url";
import { useLanguage } from "@/contexts/LanguageContext";
import { adminDisplayName, useAdminRoster } from "@/hooks/useAdminRoster";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { generateEmailReply } from "@/lib/generate-email-reply";
import {
  useWhatsAppTemplates,
  type WhatsAppTemplate,
} from "@/hooks/useWhatsAppTemplates";
import type { Database, Json } from "@/lib/supabase-types";
import {
  channelProducesDeliveryReceipt,
  serviceWindow,
  type SupportChannel,
} from "@/lib/support-views";
import { relativeTime } from "@/lib/support-time";
import { toast } from "sonner";
import {
  Smile,
  Send,
  Loader2,
  Check,
  CheckCheck,
  Paperclip,
  MailX,
  MoreVertical,
  X as XIcon,
  FileText,
  Image as ImageIcon,
  Pencil,
  Trash2,
  Ban,
  Sparkles,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EmojiPicker, { Theme, type EmojiClickData } from "emoji-picker-react";
import { supportSystemEventText } from "@/lib/support-system-events";

type IssueCommentRow = {
  id: string;
  issue_id: string;
  user_id: string;
  comment: string;
  created_at: string;
  is_internal: boolean | null;
  sender_scope?: "tenant" | "admin" | null;
  // COL-322 — edit/soft-delete markers. Optional because the generated
  // supabase types don't know these columns yet; `select("*")` returns them.
  edited_at?: string | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
  // Ticketing v2.1 email delivery markers (20260912110000_ticketing_v21_inbox.sql).
  // Any one of them set means this row already exists in someone's mailbox.
  email_message_id?: string | null;
  provider_message_id?: string | null;
  delivered_at?: string | null;
  // Written by send-ticket-reply on every failed dispatch, and by
  // whatsapp-webhook on a `failed` delivery receipt (a message Meta accepted
  // and then could not deliver — blocked number, not on WhatsApp). Nothing in
  // src/ read this column at all until the tick below, so those rows showed
  // staff an ordinary delivered bubble.
  delivery_error?: string | null;
  is_system?: boolean;
  read_at?: string | null;
};

// The one template that may be preselected: the support re-engagement nudge.
// Anything else must be picked deliberately, since it may be a marketing or
// test template.
const SUPPORT_REPLY_TEMPLATE_NAME = "support_reply_nudge";

// Two approved templates can share a name (same text, different category or
// language), so a name alone cannot identify which one staff picked.
const templateKey = (t: {
  name: string;
  language: string;
  category: string;
}) => `${t.name}:${t.language}:${t.category}`;

// Only meaningful inbound messages can make an Agent draft stale. Admin
// sends and system activity must not invalidate a draft the tenant has not
// changed. The snapshot remains order-independent and detects edits too.
type ConversationSnapshot = {
  /** Number of eligible inbound comments. */
  count: number;
  /** The newest inbound comment's created_at, or null if none exists. */
  newestCreatedAt: string | null;
  /** Tie-breaker when two comments share the same created_at. */
  newestId: string | null;
  /** Latest edit/delete timestamp among eligible inbound comments. */
  newestChangeAt: string | null;
};

type ConversationSnapshotRow = {
  id: string;
  created_at: string;
  edited_at?: string | null;
  deleted_at?: string | null;
};

const EMPTY_CONVERSATION_SNAPSHOT: ConversationSnapshot = {
  count: 0,
  newestCreatedAt: null,
  newestId: null,
  newestChangeAt: null,
};

/** Fold an eligible inbound row without relying on query ordering. */
function foldRowIntoConversationSnapshot(
  snapshot: ConversationSnapshot,
  row: ConversationSnapshotRow
): ConversationSnapshot {
  let { newestCreatedAt, newestId, newestChangeAt } = snapshot;
  if (
    newestCreatedAt === null ||
    row.created_at > newestCreatedAt ||
    (row.created_at === newestCreatedAt &&
      (newestId === null || row.id > newestId))
  ) {
    newestCreatedAt = row.created_at;
    newestId = row.id;
  }
  for (const changeAt of [row.edited_at, row.deleted_at]) {
    if (changeAt && (newestChangeAt === null || changeAt > newestChangeAt)) {
      newestChangeAt = changeAt;
    }
  }
  return {
    count: snapshot.count + 1,
    newestCreatedAt,
    newestId,
    newestChangeAt,
  };
}

function computeConversationSnapshot(
  rows: IssueCommentRow[]
): ConversationSnapshot {
  return rows
    .filter(
      (row) =>
        row.sender_scope === "tenant" &&
        !row.is_internal &&
        !row.deleted_at &&
        !!row.comment?.trim()
    )
    .reduce(foldRowIntoConversationSnapshot, EMPTY_CONVERSATION_SNAPSHOT);
}

function conversationSnapshotsEqual(
  a: ConversationSnapshot,
  b: ConversationSnapshot
): boolean {
  return (
    a.count === b.count &&
    a.newestCreatedAt === b.newestCreatedAt &&
    a.newestId === b.newestId &&
    a.newestChangeAt === b.newestChangeAt
  );
}

type TicketAttachment = {
  file_path: string;
  file_name: string;
  content_type: string;
  size: number;
  public_url: string | null;
  bucket?: string;
};

type IssueTicketSnippet = Pick<
  Database["public"]["Tables"]["issues"]["Row"],
  "id" | "title" | "description" | "metadata" | "created_at"
>;
type IssueCommentInsert =
  Database["public"]["Tables"]["issue_comments"]["Insert"] & {
    sender_scope?: "tenant" | "admin";
  };

// --- Chat message envelope (for per-message attachments) ---
// `url` is optional: whatsapp-webhook writes inbound media as `path` +
// `bucket` — a path into the private, staff-read-only whatsapp-media bucket
// — and no `url` at all (mirrors supabase/functions/_shared/ticket-reply.ts's
// ChatAttachment; a duplicate, not a cross-import — see that file's header).
// Every attachment the composer below uploads still carries a real `url`.
type ChatAttachment = {
  name: string;
  url?: string;
  type: string;
  size: number;
  path?: string;
  bucket?: string;
};
type ChatEnvelope = { v: 1; text: string; attachments: ChatAttachment[] };

function stripNulls(s: string): string {
  // PostgreSQL text columns reject null bytes
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x00/g, "");
}

function encodeEnvelope(text: string, atts: ChatAttachment[]): string {
  const t = stripNulls(text);
  if (!atts.length) return t;
  // path/bucket carried through untouched (never user-typed — the webhook
  // writes them) so re-encoding on edit doesn't silently drop a WhatsApp
  // attachment's only way of being rendered. url stays optional: the
  // composer's own uploads always set it, but nothing here may assume so.
  const safeAtts: ChatAttachment[] = atts.map((a) => ({
    name: stripNulls(a.name),
    ...(a.url != null ? { url: stripNulls(a.url) } : {}),
    type: stripNulls(a.type),
    size: a.size,
    ...(a.path != null ? { path: a.path } : {}),
    ...(a.bucket != null ? { bucket: a.bucket } : {}),
  }));
  const env: ChatEnvelope = { v: 1, text: t, attachments: safeAtts };
  return "CHATENV:" + JSON.stringify(env);
}

function decodeEnvelope(raw: string): { text: string; atts: ChatAttachment[] } {
  if (raw.startsWith("CHATENV:")) {
    try {
      const env = JSON.parse(raw.slice(8)) as ChatEnvelope;
      return { text: env.text ?? "", atts: env.attachments ?? [] };
    } catch {
      // fall through
    }
  }
  return { text: raw, atts: [] };
}
// -----------------------------------------------------------

function metadataAttachmentRecords(
  metadata: Json | null | undefined
): unknown[] {
  if (metadata === null || metadata === undefined) return [];
  if (typeof metadata !== "object" || Array.isArray(metadata)) return [];
  const att = (metadata as Record<string, unknown>).attachments;
  return Array.isArray(att) ? att : [];
}

function isImageType(contentType: string) {
  return contentType.toLowerCase().startsWith("image/");
}

function isImageAttachment(a: TicketAttachment) {
  return isImageType(a.content_type || "");
}

/** How Task 6 closes the other half of "store a path, not a URL": the
 *  private whatsapp-media bucket is staff-read-only, so a path is only ever
 *  viewable through a signed URL, and Task 5 deliberately writes `path` +
 *  `bucket` rather than a persisted signed one. Signing happens HERE, on
 *  render, via the query cache — never once at fetch/decode time and stored,
 *  which is the exact bug the broadcast attachments already have (a 1-hour
 *  signed URL persisted to the database, dead on every read after the first
 *  hour). `path` wins whenever both `path`/`bucket` and `url` are present, so
 *  a ticket's channel changing after the fact can never strand a WhatsApp
 *  attachment on a stale `url`. A url-only attachment is signed too when its
 *  url points into our own Storage — a staff upload's stored public URL into
 *  `contracts`, which is going private (COL-436). Any other url (an email
 *  attachment) renders as it is and is never signed. */
export function useChatAttachmentHref(att: ChatAttachment): string | undefined {
  const hasPath = !!(att.path && att.bucket);
  // An http(s) link that is not in our Storage needs no round trip.
  const direct =
    !hasPath &&
    att.url &&
    isOpenableUrl(att.url) &&
    !storageRefFor(att.url, "contracts")
      ? att.url
      : undefined;
  const { data: signedUrl } = useQuery({
    queryKey: ["chat-attachment-signed-url", att.bucket, att.path, att.url],
    queryFn: async () => {
      if (hasPath) {
        const { data, error } = await supabase.storage
          .from(att.bucket as string)
          .createSignedUrl(att.path as string, 3600);
        if (error) throw error;
        return data.signedUrl;
      }
      const url = await resolveStoredFileUrl(att.url as string, "contracts", {
        expiresIn: 3600,
      });
      // resolveStoredFileUrl swallows failures to null; cached as data, that
      // null would count as fresh success for 50 minutes and strand the tile
      // with no retry. Thrown instead, React Query retries and marks error.
      if (!url) throw new Error("signing failed");
      return url;
    },
    enabled: hasPath || (!!att.url && !direct),
    // Re-sign well inside the 1-hour TTL. `staleTime` alone only marks the
    // cached URL stale — without a focus/remount nothing refetches, so a
    // thread left open longer than the TTL would serve expired hrefs (these
    // links used to be permanent public URLs). The interval actually renews.
    staleTime: 50 * 60 * 1000,
    refetchInterval: 50 * 60 * 1000,
  });
  return direct ?? signedUrl ?? undefined;
}

/**
 * A ticket's own attachments (`issues.metadata.attachments[]`) are objects in
 * `contracts`' support-attachments/ folder; `file_path` is signed on read
 * (1 h), never the stored `public_url` (COL-436).
 */
function useTicketAttachmentHref(a: TicketAttachment): string | undefined {
  const { data } = useQuery({
    queryKey: ["ticket-attachment-signed-url", a.bucket, a.file_path],
    queryFn: async () => {
      const bucket = a.bucket || "contracts";
      const url =
        bucket === "contracts"
          ? await resolveStoredFileUrl(a.file_path, "contracts", {
              expiresIn: 3600,
            })
          : (
              await supabase.storage
                .from(bucket)
                .createSignedUrl(a.file_path, 3600)
            ).data?.signedUrl;
      // Null is a swallowed failure, not data — throw so React Query retries
      // instead of caching it as 50 minutes of fresh success (see above).
      if (!url) throw new Error("signing failed");
      return url;
    },
    // Interval, not just staleTime: renew the href while the thread stays
    // mounted past the TTL (see the chat-attachment query above).
    staleTime: 50 * 60 * 1000,
    refetchInterval: 50 * 60 * 1000,
  });
  return data ?? undefined;
}

/** One ticket attachment tile. Its own component: the signing hook above. */
function TicketAttachmentTile({ a }: { a: TicketAttachment }) {
  const { t } = useLanguage();
  const href = useTicketAttachmentHref(a);
  if (isImageAttachment(a) && href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="group relative overflow-hidden rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/30 transition-colors"
        title={a.file_name}
      >
        <img
          src={href}
          alt={a.file_name}
          className="h-28 w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent p-2">
          <div className="text-[11px] text-white/95 truncate">
            {a.file_name}
          </div>
        </div>
      </a>
    );
  }
  return (
    <a
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel={href ? "noreferrer" : undefined}
      className={cn(
        "rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/30 transition-colors p-3 flex items-center gap-2",
        href ? "" : "opacity-60 pointer-events-none"
      )}
      title={a.file_name}
    >
      <div className="h-9 w-9 rounded-lg bg-background border border-border/60 flex items-center justify-center text-xs font-semibold">
        {a.content_type.toLowerCase().includes("pdf")
          ? t("issueChatThread.pdf", "PDF")
          : t("issueChatThread.file", "FILE")}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{a.file_name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {a.content_type}
        </div>
      </div>
    </a>
  );
}

/** One attachment bubble in the thread. Its own component (not inlined in
 *  the comments .map below) because useChatAttachmentHref calls a hook —
 *  every rendered attachment needs its own query, which only works with one
 *  component instance per attachment. */
function ChatAttachmentBubble({ att }: { att: ChatAttachment }) {
  const href = useChatAttachmentHref(att);
  if (isImageType(att.type) && href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="block">
        <img
          src={href}
          alt={att.name}
          className="max-w-[220px] rounded-xl object-cover"
          loading="lazy"
        />
        <div className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[220px]">
          {att.name}
        </div>
      </a>
    );
  }
  return (
    <a
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel={href ? "noreferrer" : undefined}
      className="flex items-center gap-2 rounded-lg bg-black/5 px-2 py-1.5 hover:bg-black/10 transition-colors"
    >
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-xs truncate max-w-[180px]">{att.name}</span>
    </a>
  );
}

function formatTime(dateStr: string, language: "en" | "de") {
  try {
    return new Date(dateStr).toLocaleTimeString(
      language === "de" ? "de-DE" : undefined,
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return dateStr;
  }
}

function formatDayPill(
  dateStr: string,
  t: (key: string, fallback?: string) => string,
  language: "en" | "de"
) {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const locale = language === "de" ? "de-DE" : undefined;
    const startOfDay = (x: Date) =>
      new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round(
      (startOfDay(now) - startOfDay(d)) / (24 * 60 * 60 * 1000)
    );
    if (days === 0) return t("issueChatThread.today", "Today");
    if (days === 1) return t("issueChatThread.yesterday", "Yesterday");
    if (days > 1 && days < 7)
      return d.toLocaleDateString(locale, { weekday: "long" });
    return d.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
  } catch {
    return dateStr;
  }
}

/** Mirrors supabase/functions/_shared/ticket-reply.ts's SEND_AS_IDENTITIES —
 *  keep the two lists in sync. The edge function re-validates whatever this
 *  picker sends (resolveFromAddress), so a stale list here can only ever
 *  under-offer a choice, never smuggle an unapproved From address through. */
const SEND_AS_IDENTITIES = ["info@coliviq.de", "kontakt@coliviq.de"] as const;
type SendAsIdentity = (typeof SEND_AS_IDENTITIES)[number];

/** Outcome of dispatching one reply comment through send-ticket-reply,
 *  keyed by that comment's id — see the dispatchReply/sendState comment
 *  below for why this map may ONLY ever be populated with ids this
 *  component itself just inserted. */
type ReplySendState =
  // `attachmentsDropped` mirrors the wire field: present only when one or
  // more files could not be delivered under the channel's media rules.
  | { status: "sending" }
  | { status: "sent"; attachmentsDropped?: boolean }
  // Email left the building but either the DB writeback failed, or this was
  // a 409 (already claimed/delivered — us moments ago, or a race). Either
  // way the tenant may already have it: never render a Retry here, or a
  // click could send a second real email.
  | { status: "sent-unconfirmed"; attachmentsDropped?: boolean }
  // 200 sent:false — e.g. a "ticket"-channel reply (portal-only) or
  // whatsapp (schema-ready, no provider yet). Not a failure: the reply is
  // saved and visible in the thread, it just has nowhere to go out on.
  // `reason` is the server's decision.reason (I2 fix) — kept so the bubble
  // can tell a merely-informational portal-only skip apart from a
  // genuinely undelivered reply instead of rendering both identically.
  | { status: "skipped"; reason?: string }
  // `retryable` false for the two machine-readable WhatsApp refusals
  // (window_closed, empty_message) — retrying without changing anything
  // would fail again the exact same way, so the Retry link is withheld
  // rather than offering an action that cannot succeed (see the brief:
  // "Nobody should type an answer that cannot be delivered" — extended
  // here to "nobody should be offered a retry that cannot work"). Absent
  // (or true) for every other failure, where a retry is a normal transient-
  // error recovery (network blip, Resend/Graph API hiccup).
  | { status: "failed"; message: string; retryable?: boolean };

// I2 fix — the server's 200 sent:false response collapses two different
// outcomes into one client status ("skipped"): a portal-only ticket
// (channel has no outbound method at all — expected, silent) and a
// schema-ready-but-unwired channel like whatsapp (genuinely not delivered).
// planReplyDispatch's "unsupported" branch (_shared/ticket-reply.ts) is the
// only producer of this exact reason string; "skip"'s reason is always the
// dynamic `channel "X" has no outbound delivery` shape instead.
// ponytail: string-matches the one known "not delivered" reason rather than
// adding a `kind` discriminant to the wire response — revisit if a second
// unsupported-channel reason is ever introduced.
function isUndeliveredSkip(state: ReplySendState | undefined): boolean {
  return state?.status === "skipped" && state.reason === "channel not enabled";
}

export function IssueChatThread(props: {
  issueId: string | null | undefined;
  currentUserId: string;
  queryKeyPrefix: string;
  hideInternal?: boolean;
  /** Opposite of hideInternal: show ONLY internal notes, and force every
   *  message posted through this instance to `is_internal = true` regardless
   *  of canPostInternal/the toggle. For a dedicated "Internal notes" surface
   *  (ticketing v21 Task 6) where the tab boundary itself is what keeps a
   *  note off the tenant-visible thread — no per-message toggle to get
   *  wrong. */
  internalOnly?: boolean;
  canPostInternal?: boolean;
  readOnly?: boolean;
  hideHeader?: boolean;
  supportAppearance?: boolean;
  showViewBadge?: boolean;
  showInternalToggle?: boolean;
  markReadOnOpen?: boolean;
  readScope?: "tenant" | "admin";
  senderScope?: "tenant" | "admin";
  /** The ticket's own arrival channel — only used to show the FROM picker
   *  (email only) above the composer. Omit on any surface that doesn't need
   *  it (internal notes, tenant-facing threads): undefined simply never
   *  matches "email", so the picker stays hidden by default. */
  channel?: SupportChannel;
  /** Opt-in gate for the two things that leave the application: the real
   *  send-ticket-reply dispatch and the "waiting" status write. Read-only,
   *  internal-note, and tenant surfaces stay inert unless their caller opts
   *  in. Keeping the decision at the call site makes this shared component
   *  reusable and unit-testable. */
  dispatchOutbound?: boolean;
  canMarkUnread?: boolean;
  composerLabel?: string;
  composerPlaceholder?: string;
  sendLabel?: string;
  supportLabel?: string;
  youLabel?: string;
  adminLabel?: string;
  tenantLabel?: string;
  /** Optional ticket-specific content rendered after the last message and
   *  before the composer, inside the conversation scroll region. */
  conversationFooter?: React.ReactNode;
  /** Copy used by the compact support composer while it is an internal note. */
  internalNotePlaceholder?: string;
  translatedFrom?: string;
  originalDescription?: string;
  onOutboundSent?: () => void;
  /** Search results can open a thread at the matching message. */
  focusCommentId?: string | null;
}) {
  const { t, language } = useLanguage();
  // Narrowed to the base language: this file's helpers predate the six-language
  // widening and still take en|de. (COL-477 port.)
  const baseLang = baseLanguageFor(language);
  const {
    issueId,
    currentUserId,
    queryKeyPrefix,
    hideInternal = false,
    internalOnly = false,
    canPostInternal = false,
    channel,
    dispatchOutbound = false,
    readOnly = false,
    hideHeader = false,
    supportAppearance = false,
    showViewBadge = true,
    showInternalToggle = false,
    markReadOnOpen = true,
    readScope = "tenant",
    senderScope = "tenant",
    composerLabel = t("issueChatThread.yourReply", "Your reply"),
    composerPlaceholder = t(
      "issueChatThread.writeAMessage",
      "Write a message…"
    ),
    sendLabel = t("issueChatThread.sendReply", "Send reply"),
    supportLabel = t("issueChatThread.support", "SUPPORT"),
    youLabel = t("issueChatThread.you", "YOU"),
    adminLabel = t("issueChatThread.admin", "ADMIN"),
    tenantLabel,
    conversationFooter,
    internalNotePlaceholder = t(
      "issueChatThread.internalNotePlaceholder",
      "Note for the team — the tenant won't see this"
    ),
    translatedFrom,
    originalDescription,
    onOutboundSent,
    canMarkUnread = false,
  } = props;
  const threadRootRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { roster: mentionRoster } = useAdminRoster(supportAppearance);
  const [draftActionTarget, setDraftActionTarget] =
    useState<HTMLSpanElement | null>(null);
  const [message, setMessage] = useState("");
  // Mentions are composer tokens, not text the operator has to edit around.
  // They are serialized to the established @Name wire format only when the
  // internal note is saved, so the notification trigger keeps working while
  // the UI can render/removal them as real chips.
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [showOriginalDescription, setShowOriginalDescription] = useState(false);
  const [postInternal, setPostInternal] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [chatFiles, setChatFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  // coliviq Agent — docked panel above the composer. `assistReplyText` and
  // `assistNotice`/`assistError` persist across Close/reopen (only
  // `assistOpen`/`assistIsEditing` reset): see `assistGeneratedSnapshotRef`
  // below for why a draft is generated at most once per ticket, not once per
  // open.
  const [assistOpen, setAssistOpen] = useState(false);
  const [assistReplyText, setAssistReplyText] = useState("");
  const [assistIsEditing, setAssistIsEditing] = useState(false);
  const [assistEditDraft, setAssistEditDraft] = useState("");
  const [assistError, setAssistError] = useState<string | null>(null);
  const [assistNotice, setAssistNotice] = useState<string | null>(null);
  // Codex review finding: a same-comment retry (see assistFailedSendRef
  // below) dispatches via dispatchReply directly, bypassing postComment
  // entirely — so postComment.isPending never reflects it, and isSendInFlight
  // below would otherwise leave Send now clickable for the retry's own
  // duration. A second click mid-retry would race the server's per-comment
  // delivery claim: the second call can get back a 409 ("already claimed",
  // mapped to sent-unconfirmed) before the first call's real outcome is
  // known, closing the panel on what might still turn out to be a failure.
  const [isAssistRetryDispatching, setIsAssistRetryDispatching] =
    useState(false);
  // COL-322 — inline edit + delete-confirm state for the caller's own messages.
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null
  );
  // Task 10 — reply dispatch. `fromAddress` is null until the admin actively
  // picks one: left null, `dispatchReply` sends no override at all, so the
  // edge function's own resolveFromAddress (ticket's real arrival address)
  // decides — the picker's displayed value only ever falls back to
  // SEND_AS_IDENTITIES[0] for rendering, never duplicating that lookup
  // client-side. `sendState` is keyed by comment id and is written ONLY by
  // dispatchReply below, whose only two call sites are (1) immediately
  // after this component's own insert, with the id that insert just
  // returned, and (2) the bubble's own Retry action for an id already a key
  // in this map. Nothing ever seeds it from the fetched `comments` list, so
  // no historical or system-authored row's id can ever end up here — see
  // the dispatchReply comment for the full argument.
  const [fromAddress, setFromAddress] = useState<SendAsIdentity | null>(null);
  const [sendState, setSendState] = useState<Record<string, ReplySendState>>(
    {}
  );

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const emojiPanelRef = useRef<HTMLDivElement | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Generates once per *conversation state*, not once per open/close —
  // closing and reopening the panel must show the same (possibly
  // admin-edited) draft, never silently regenerate over it, UNLESS the
  // conversation has changed since the last generation (tracked by comparing
  // a ConversationSnapshot — see the type's own comment above — not just the
  // latest comment's id), in which case the cached draft/no_reply
  // recommendation is stale and must be regenerated — see the auto-generate
  // effect below.
  //
  // Review finding (Ganesh, consolidation): this used to be TWO refs — a
  // boolean "has a generation been requested" plus a separate "signature it
  // was generated for" — and some paths (the onError retry-reset, in
  // particular) reset only the boolean, leaving the other one holding a
  // stale snapshot the two could silently disagree about. A single
  // nullable ref is now the one source of truth: null means no valid
  // generated result exists for the current conversation state; a stored
  // snapshot means generation happened for exactly that state. Reset to
  // null when a generation attempt actually failed, so a transient error
  // gets a fresh automatic retry on next open instead of being stuck
  // forever — and on every other path that used to clear the boolean.
  const assistGeneratedSnapshotRef = useRef<ConversationSnapshot | null>(null);
  // Review finding: a close+reopen while a generation is still in flight (a
  // new comment arrived mid-request, the admin reopened before it resolved)
  // starts a SECOND request without cancelling the first. Whichever
  // resolves later would otherwise win regardless of which is actually
  // newer — the comment-id ref doesn't protect against this because it's
  // only checked when a request starts, not when one finishes. Each
  // generateDraft.mutate() call is tagged with the current value here, and
  // its callbacks below no-op if a newer request has since started.
  const assistRequestEpochRef = useRef(0);
  // Codex review finding: a failed Send now used to be retried by calling
  // postComment again, which inserts a BRAND NEW comment row and dispatches
  // that — so a false-negative "failed" (the reply actually went out but the
  // HTTP response was lost) turns a retry into a genuine duplicate reply to
  // the tenant. The existing per-message chat bubble already solves this
  // (its own "Not sent" → Retry button calls dispatchReply on the SAME
  // comment id with isRetry: true, which the server's 409 idempotency check
  // backs — see dispatchReply's own comment above). This ref lets Send now
  // reuse that exact path: set on a failed send, consulted on the next click
  // (only when the draft text is unchanged — an edited retry is a genuinely
  // different message and must go through postComment as a new send), and
  // cleared on success or whenever a fresh draft is generated.
  // Review finding: `retryable` mirrors the per-message chat bubble's own
  // dispatchOutcome.retryable (false only for the two machine-readable
  // WhatsApp refusals — see dispatchReply's own comment) — Send now must be
  // disabled on THIS unchanged draft when it's false, the same way the
  // bubble withholds its own Retry action, until an edit or a fresh
  // generation clears this ref.
  const assistFailedSendRef = useRef<{
    id: string;
    text: string;
    retryable: boolean;
  } | null>(null);

  // coliviq Agent is a property of the shared composer, not of any one
  // channel: it must work for email, WhatsApp, and ticket/portal alike, in
  // both assigned and unassigned conversations, in every inbox view — none
  // of which this component can see directly, since `dispatchOutbound` is
  // already the one flag the call site (TicketDetail's Conversation tab)
  // uses to mean "this is a real, replyable admin composer" regardless of
  // channel/view/assignment. Gating on `channel` here would hardcode a
  // per-channel allowlist the product has explicitly rejected — see
  // knowledge/implementation-notes/coliviq-assist-sparkle.md §5.
  const canUseAiAssist =
    !readOnly &&
    !internalOnly &&
    dispatchOutbound &&
    senderScope === "admin" &&
    !!issueId;

  const generateDraft = useMutation({
    mutationFn: async (epoch: number) => {
      if (!issueId) throw new Error("Issue is required");
      const result = await generateEmailReply(issueId);
      return { result, epoch };
    },
    onSuccess: ({ result, epoch }) => {
      // Superseded by a newer request started since this one began (see
      // assistRequestEpochRef's own comment) — its own callback already
      // applied the up-to-date outcome; applying this stale one on top
      // would silently revert it.
      if (epoch !== assistRequestEpochRef.current) return;
      if (result.action === "no_reply") {
        // Review finding: always the translated application message, never
        // the model's own free-form `reason` verbatim — a raw model string
        // reaching the admin UI untranslated (and unreviewed) is exactly the
        // kind of model output this panel is built to keep off the direct
        // path to a human-facing surface. The reason still reaches
        // diagnostics via this console.debug, just never replaces the UI
        // text.
        console.debug("[IssueChatThread] coliviq Agent no_reply reason:", {
          reason: result.reason,
        });
        setAssistNotice(
          t(
            "issueChatThread.assistNoReply",
            "No reply is recommended for this message."
          )
        );
        setAssistError(null);
        return;
      }
      setAssistReplyText(result.replyBody);
      setAssistNotice(null);
      setAssistError(null);
    },
    onError: (error, epoch) => {
      if (epoch !== assistRequestEpochRef.current) return;
      console.error(
        "[IssueChatThread] coliviq Agent draft generation failed:",
        error
      );
      setAssistError(
        t(
          "issueChatThread.assistFailure",
          "The reply draft could not be created."
        )
      );
      setAssistNotice(null);
      // Allow a fresh attempt — either the visible Retry button (review
      // finding: Ganesh) or the next time the panel opens — see
      // assistGeneratedSnapshotRef's own comment for why success/no_reply
      // do not.
      assistGeneratedSnapshotRef.current = null;
    },
  });

  // Outside-click + Escape for emoji panel
  useEffect(() => {
    if (!emojiOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (emojiPanelRef.current?.contains(target)) return;
      if (emojiButtonRef.current?.contains(target)) return;
      setEmojiOpen(false);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setEmojiOpen(false);
      }
    };

    const id = window.setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDown);
    }, 0);
    document.addEventListener("keydown", onKey, true);

    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [emojiOpen]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [message]);

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setMessage((m) => `${m}${emoji}`);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + emoji + el.value.slice(end);
    setMessage(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const { data: ticket } = useQuery({
    queryKey: [queryKeyPrefix, "issue", issueId],
    queryFn: async () => {
      if (!issueId) return null;
      const { data, error } = await supabase
        .from("issues")
        .select("id, title, description, metadata, created_at")
        .eq("id", issueId)
        .single();
      if (error) throw error;
      return data as IssueTicketSnippet | null;
    },
    enabled: !!issueId,
    staleTime: 30_000,
  });

  const attachments = useMemo(() => {
    const raw = metadataAttachmentRecords(ticket?.metadata);
    return raw
      .filter(
        (item): item is Record<string, unknown> =>
          typeof item === "object" && item !== null && !Array.isArray(item)
      )
      .map(
        (a): TicketAttachment => ({
          file_path: String(a.file_path ?? ""),
          file_name: String(
            a.file_name ?? t("issueChatThread.attachmentFallback", "Attachment")
          ),
          content_type: String(a.content_type ?? "application/octet-stream"),
          size: Number(a.size ?? 0),
          public_url: a.public_url != null ? String(a.public_url) : null,
        })
      )
      .filter((a) => !!a.file_path);
  }, [ticket, t]);

  const queryKey = useMemo(
    () =>
      [
        queryKeyPrefix,
        "issue-comments",
        issueId,
        hideInternal,
        internalOnly,
      ] as const,
    [queryKeyPrefix, issueId, hideInternal, internalOnly]
  );

  const { data: comments, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!issueId) return [];
      let query = supabase
        .from("issue_comments")
        .select("*")
        .eq("issue_id", issueId)
        .order("created_at", { ascending: true });

      if (internalOnly) {
        query = query.eq("is_internal", true);
      } else if (hideInternal) {
        query = query.or("is_internal.is.null,is_internal.eq.false");
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as IssueCommentRow[];
    },
    enabled: !!issueId,
  });

  const currentConversationSnapshot = useMemo(
    () => computeConversationSnapshot(comments ?? []),
    [comments]
  );

  // Review finding (Ganesh, finding 4): the auto-generate effect below and
  // the visible Retry button share this exact "start a fresh generation for
  // THIS conversation snapshot" sequence — factored out once so the two
  // can't drift, and so the Retry button reuses the same request-epoch and
  // failed-send-reset protections the automatic path already has.
  const startDraftGeneration = (snapshot: ConversationSnapshot) => {
    assistGeneratedSnapshotRef.current = snapshot;
    // A fresh generation means a new draft/conversation baseline — any
    // earlier failed-send tracking (see assistFailedSendRef above) no longer
    // refers to this draft's own content.
    assistFailedSendRef.current = null;
    assistRequestEpochRef.current += 1;
    generateDraft.mutate(assistRequestEpochRef.current);
  };

  // Auto-drafts the moment the panel opens — the approved design has no
  // instruction field or manual Generate button (contrast the old
  // email-only, manually-triggered version this replaces). Review finding:
  // comparing a ConversationSnapshot (not just the latest id) catches a new
  // tenant message that arrived since the last generation (or since a plain
  // Send, which isn't routed through assistGeneratedSnapshotRef at all), AND
  // an edit or soft-delete of an existing message that the latest-id check
  // alone would miss (edits/deletes keep the same id, but bump
  // newestChangeAt) — without this, the panel could keep showing a stale "no
  // reply needed" recommendation, or a stale draft that doesn't address what
  // the tenant actually asked or has since retracted. This effect only fires
  // on an assistOpen TRANSITION (or once `isLoading` clears, see below) —
  // not on every `currentConversationSnapshot` change — see `assistIsStale`
  // below for the while-already-open case (TicketDetail's "ticket-detail"
  // query is also invalidated live by useSupportInboxRealtime's
  // issue_comments subscription in src/hooks/useSupportInbox.ts, so a new
  // message can arrive while this panel is already open, not just between
  // opens).
  //
  // Review finding: `isLoading` guards the comments query's own initial
  // fetch. Without it, opening the panel before that query settles would
  // compute the snapshot from `comments === undefined` (treated as "no
  // comments", same as a genuinely empty ticket), generate against an
  // incomplete/wrong baseline, AND record that wrong baseline into
  // assistGeneratedSnapshotRef — so the moment the real comments then load
  // with a non-empty snapshot, assistIsStale would immediately read true
  // even though nothing new actually arrived. `isLoading` is in the
  // dependency array (not just checked inline) so that if the panel is
  // opened while still loading, generation fires as soon as loading clears
  // instead of requiring a close+reopen.
  useEffect(() => {
    if (!assistOpen) return;
    if (isLoading) return;
    if (
      assistGeneratedSnapshotRef.current !== null &&
      conversationSnapshotsEqual(
        currentConversationSnapshot,
        assistGeneratedSnapshotRef.current
      )
    ) {
      return;
    }
    startDraftGeneration(currentConversationSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistOpen, isLoading]);

  // Review finding: a new message can arrive (via realtime) while the panel
  // is already open, which the effect above — deliberately open-transition-
  // only, so it never fires mid-edit and clobbers what the admin is typing —
  // does not catch. Surface it instead of silently regenerating: an
  // auto-regeneration could overwrite an active edit or replace a draft the
  // admin was about to send without warning. Purely informational — no new
  // action; Close + reopen (already covered above) or Edit are how the admin
  // responds to it.
  const assistIsStale =
    assistOpen &&
    !generateDraft.isPending &&
    !assistError &&
    assistGeneratedSnapshotRef.current !== null &&
    !conversationSnapshotsEqual(
      currentConversationSnapshot,
      assistGeneratedSnapshotRef.current
    );

  // Task 9 — the WhatsApp service window, mirrored off send-ticket-reply's
  // own derivation: the newest TENANT-scoped comment's created_at, never a
  // stored column (constraint: don't re-derive this differently from the
  // server, or the UI and the server can disagree about whether a send is
  // possible).
  //
  // Review round 1 finding — this used to trust `comments`' own array
  // position (`tenantRows[tenantRows.length - 1]`), on the assumption that
  // the query's `order("created_at", {ascending:true})` guarantees it's
  // already last. A reviewer proved that assumption isn't a safe one to
  // bake into the SELECTION logic itself (seeded two tenant comments out of
  // chronological order — exactly what a flipped `ascending` elsewhere, a
  // stale cache, or an unstable sort on tied timestamps could produce — and
  // the unmodified code read a 2-hour-old window as closed). Comparing
  // parsed timestamps explicitly is correct regardless of what order the
  // array happens to arrive in, and costs nothing extra: `comments` is
  // already fetched once for the thread either way.
  const lastTenantMessageAt = useMemo(() => {
    let latest: string | null = null;
    let latestMs = -Infinity;
    for (const c of comments ?? []) {
      if (c.sender_scope !== "tenant") continue;
      const ms = Date.parse(c.created_at);
      if (Number.isFinite(ms) && ms > latestMs) {
        latestMs = ms;
        latest = c.created_at;
      }
    }
    return latest;
  }, [comments]);

  // Review round 1, finding 3 — the client's own `serviceWindow` read is
  // only ever an optimisation (show remaining time, hide the box early);
  // the SERVER's answer at send time is the authoritative one. An admin who
  // opens a ticket with, say, 2 minutes left, types for 3, and presses
  // send is not racing anything — the client's belief is just stale by
  // the time the request lands, and `send-ticket-reply` is right to refuse
  // it. Previously the "Send WhatsApp notification" button was gated
  // purely on the client's own (by-then-wrong) belief that the window was
  // still open, so a `window_closed` refusal in that shape left the admin
  // with a Retry that's withheld and no other action that could work.
  // `dispatchReply` flips this to true the moment the server says so (see
  // its own comment); it resets whenever the tenant sends a new message
  // (`lastTenantMessageAt` changes) — the one real-world event that can
  // actually reopen the window.
  const [serverWindowClosed, setServerWindowClosed] = useState(false);
  useEffect(() => {
    setServerWindowClosed(false);
  }, [lastTenantMessageAt]);

  // …falling back to the TICKET's own created_at when no tenant comment
  // exists, which on WhatsApp is the dominant path rather than an edge case:
  // whatsapp-webhook creates a ticket from a text-only message WITHOUT
  // writing any issue_comments row (the text goes into issues.description),
  // and unknown-sender linking preserves that same shape. A WhatsApp ticket exists BECAUSE a
  // tenant messaged, so its created_at IS a tenant message time. Without
  // this, the first reply to every brand-new conversation replaced the
  // composer with the "it's been over 24 hours" panel — about a message that
  // arrived seconds ago. send-ticket-reply/index.ts applies the identical
  // fallback at its own call site (`serviceWindow`'s signature is untouched
  // on purpose: it is mirrored across src/ ↔ supabase/functions/ and pinned
  // by support-views.parity.test.ts). Keep the two in step.
  const whatsappWindowAnchor =
    lastTenantMessageAt ?? ticket?.created_at ?? null;

  // `dispatchOutbound` gates this the same way it gates the two things that
  // actually leave the building elsewhere in this file: a read-only render
  // site such as the activity feed in AdminTenants.tsx must not offer a "Send
  // WhatsApp notification" button that would, since the button below calls
  // dispatchReply exactly like a real reply does. `comments !== undefined`
  // avoids one loading-flash render where no tenant comment has arrived yet
  // reading as "never messaged" for a ticket that actually has one;
  // `ticket !== undefined` does the same for the fallback above, whose
  // absence mid-load would read a brand-new ticket as closed.
  const whatsappWindowState =
    !internalOnly &&
    !(supportAppearance && postInternal) &&
    channel === "whatsapp" &&
    dispatchOutbound &&
    comments !== undefined &&
    ticket !== undefined
      ? serviceWindow(whatsappWindowAnchor, new Date())
      : null;
  // The server's word wins: closed if the client's own read says so OR the
  // server has already told this session so once (and no newer tenant
  // message has arrived since to earn back the benefit of the doubt).
  const whatsappWindowClosed =
    (whatsappWindowState !== null && !whatsappWindowState.open) ||
    (whatsappWindowState !== null && serverWindowClosed);

  // ── out-of-window template picker ────────────────────────────────────────
  // Fetched from Meta's approved list so the active language/body are known.
  // Every approved template is offered, so staff can pick any of them.
  const templatesQuery = useWhatsAppTemplates(
    whatsappWindowClosed && senderScope === "admin"
  );
  const templatesResult = templatesQuery.data;
  const templates: WhatsAppTemplate[] =
    templatesResult?.status === "ok" ? templatesResult.templates : [];
  // `unavailable` (503/502) is NOT the same as an empty list: it means we
  // could not ask, so the composer keeps the old single-button behaviour
  // against the constant rather than removing the only way to reach a tenant.
  const templatesUnavailable = templatesResult?.status === "unavailable";
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>("");
  const [templateVars, setTemplateVars] = useState<Record<string, string[]>>(
    {}
  );

  const selectedTemplate =
    templates.find((t) => templateKey(t) === selectedTemplateKey) ??
    (templates.length === 1 &&
    templates[0].name === SUPPORT_REPLY_TEMPLATE_NAME
      ? templates[0]
      : undefined);

  const currentVars = selectedTemplate
    ? (templateVars[templateKey(selectedTemplate)] ?? []).slice(
        0,
        selectedTemplate.variableCount
      )
    : [];
  const varsComplete = selectedTemplate
    ? Array.from({ length: selectedTemplate.variableCount }).every(
        (_, i) => (currentVars[i] ?? "").trim().length > 0
      )
    : false;

  const setVarAt = (name: string, index: number, value: string) =>
    setTemplateVars((prev) => {
      const next = [...(prev[name] ?? [])];
      next[index] = value;
      return { ...prev, [name]: next };
    });

  /** The body as the tenant will see it: `{{n}}` replaced by what was typed. */
  const templatePreview = selectedTemplate
    ? selectedTemplate.bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (whole, n) => {
        const v = (currentVars[Number(n) - 1] ?? "").trim();
        return v || whole;
      })
    : "";

  // Name the staff member behind each staff bubble — but only on staff
  // surfaces. Gated on `senderScope`, never on `hideInternal`: TicketDetail's
  // Conversation tab passes hideInternal AS an admin, while every tenant mount
  // (SupportTickets, SettingsSupportTickets) passes
  // senderScope="tenant" — so on the portal this lookup never fires and no
  // admin name can reach a tenant's DOM. `issue_comments.user_id` references
  // auth.users, not public.profiles, so PostgREST cannot embed across it:
  // second query by id, same as useAdminRoster / useSupportInbox.
  const staffAuthorIds = useMemo(() => {
    if (senderScope !== "admin") return [] as string[];
    const ids = new Set<string>();
    for (const c of comments ?? []) {
      if (c.sender_scope === "admin") ids.add(c.user_id);
    }
    return Array.from(ids).sort();
  }, [comments, senderScope]);

  const { data: staffAuthors } = useQuery({
    queryKey: ["issue-comment-authors", staffAuthorIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, email")
        .in("id", staffAuthorIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((p) => [p.id, p]));
    },
    enabled: staffAuthorIds.length > 0,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!comments?.length) return;
    if (props.focusCommentId) {
      const target = threadRootRef.current?.querySelector<HTMLElement>(
        `[data-comment-id="${props.focusCommentId.replace(/[^a-zA-Z0-9-]/g, "")}"]`
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.classList.add("support-search-focus");
        const timer = window.setTimeout(
          () => target.classList.remove("support-search-focus"),
          1800
        );
        return () => window.clearTimeout(timer);
      }
    }
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [comments?.length, props.focusCommentId]);

  // Mark thread as read when opened
  useEffect(() => {
    if (!markReadOnOpen) return;
    if (!issueId || !currentUserId) return;
    void (async () => {
      const nowIso = new Date().toISOString();
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            `support:lastRead:${readScope}:${currentUserId}:${issueId}`,
            nowIso
          );
        } catch {
          // ignore
        }
      }
      type IssueReadsUpsert = {
        upsert: (
          values: { issue_id: string; user_id: string; last_read_at: string },
          options: { onConflict: string }
        ) => Promise<{ error: { message: string } | null }>;
      };
      type IssueReadsClient = {
        from: (table: "issue_reads") => IssueReadsUpsert;
      };
      const { error } = await (supabase as unknown as IssueReadsClient)
        .from("issue_reads")
        .upsert(
          { issue_id: issueId, user_id: currentUserId, last_read_at: nowIso },
          { onConflict: "issue_id,user_id" }
        );
      if (error) {
        console.warn("Failed to mark issue read:", error);
      }
      if (supportAppearance && readScope === "admin") {
        const { error: teamReadError } = await supabase
          .from("issues")
          .update({ support_read_at: nowIso })
          .eq("id", issueId);
        if (teamReadError)
          console.warn("Failed to mark shared support read:", teamReadError);
      }
      await queryClient.invalidateQueries({
        queryKey: ["tenant", "issues", "issue-unread"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-issues", "issue-unread"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-issues", "support-unread-total"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["tenant", "issues", "support-unread-total"],
      });
    })();
  }, [
    issueId,
    currentUserId,
    markReadOnOpen,
    queryClient,
    readScope,
    supportAppearance,
  ]);

  const markUnreadFrom = async (createdAtIso: string) => {
    if (!issueId || !currentUserId) return;
    const targetMs = Date.parse(createdAtIso);
    if (!Number.isFinite(targetMs)) return;
    const rewindIso = new Date(targetMs - 1).toISOString();

    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          `support:lastRead:${readScope}:${currentUserId}:${issueId}`,
          rewindIso
        );
      } catch {
        // private mode
      }
    }

    type IssueReadsUpsert = {
      upsert: (
        values: { issue_id: string; user_id: string; last_read_at: string },
        options: { onConflict: string }
      ) => Promise<{ error: { message: string } | null }>;
    };
    type IssueReadsClient = {
      from: (table: "issue_reads") => IssueReadsUpsert;
    };
    const { error } = await (supabase as unknown as IssueReadsClient)
      .from("issue_reads")
      .upsert(
        { issue_id: issueId, user_id: currentUserId, last_read_at: rewindIso },
        { onConflict: "issue_id,user_id" }
      );
    if (error) {
      console.warn("Failed to mark issue unread:", error);
      toast.error(
        t("issueChatThread.toastMarkUnreadFailed", "Couldn't mark as unread")
      );
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["admin-issues", "issue-unread"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["admin-issues", "support-unread-total"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["tenant", "issues", "issue-unread"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["tenant", "issues", "support-unread-total"],
    });
    toast.success(t("issueChatThread.toastMarkedUnread", "Marked as unread"));
  };

  const markTicketUnread = async () => {
    const list = comments || [];
    if (!list.length) {
      toast.info(
        t(
          "issueChatThread.toastNothingToMarkUnread",
          "Nothing to mark unread yet."
        )
      );
      return;
    }
    const latest = list[list.length - 1];
    if (!latest?.created_at) return;
    await markUnreadFrom(latest.created_at);
  };

  // Task 10 — actually dispatch a reply comment over its ticket's channel.
  // AWAITED by design (never fire-and-forget, unlike the notify/auto-assign
  // calls below): a reply that silently fails to reach the tenant is the
  // exact bug this feature exists to fix, so the outcome is always read and
  // recorded in `sendState`, never dropped on the floor.
  //
  // The ONLY two call sites for this function are: (1) postComment's own
  // mutationFn, immediately after its `issue_comments.insert(...).select()`
  // returns a freshly-created row's id, and (2) the bubble's Retry action
  // further down, which only renders for an id that is ALREADY a key in
  // `sendState` — and the only writer of `sendState` is this function
  // itself. So by construction, every commentId that ever reaches
  // send-ticket-reply from this client is an id this component inserted in
  // this session; nothing here ever reads an id out of the fetched
  // `comments` list to build that request. That closes the gap Task 9 left
  // open: SupportInbox.tsx's synthetic "📌 Assigned to X" note is inserted
  // by a completely different code path (handleAssign) that never calls
  // this function, so its id can never reach send-ticket-reply through this
  // feature no matter what the row's is_internal/sender_scope look like.
  //
  // I1 fix — this used to be fire-and-forget from mutationFn's point of
  // view (return type void): every branch below already recorded its own
  // outcome into `sendState` and toasted its own error, but nothing told
  // the caller whether the send actually worked. Now every branch returns
  // the same ReplySendState it just wrote, so postComment's mutationFn can
  // thread it through and skip the "Message sent" toast on a real failure.
  const dispatchReply = async (
    commentId: string,
    from: SendAsIdentity | null,
    opts: {
      isRetry?: boolean;
      templateVariables?: string[];
      /** Chosen from Meta's approved list; absent = server-side constant. */
      template?: { name: string; language: string };
    } = {}
  ): Promise<ReplySendState> => {
    setSendState((s) => ({ ...s, [commentId]: { status: "sending" } }));
    const genericFailure = t(
      "issueChatThread.sendReplyFailed",
      "Couldn't send this reply"
    );
    try {
      const { data, error } = await supabase.functions.invoke<{
        sent?: boolean;
        reason?: string;
        error?: string;
        writebackFailed?: boolean;
        attachmentsDropped?: boolean;
      }>("send-ticket-reply", {
        body: {
          commentId,
          fromAddress: from ?? undefined,
          // Task 9 — only sent at all when the caller explicitly asked for
          // the template (the window-closed path): presence, not
          // non-emptiness, is send-ticket-reply's own signal to use it, so
          // an unrelated open-window send must never include this key.
          ...(opts.templateVariables !== undefined
            ? { templateVariables: opts.templateVariables }
            : {}),
          // Name AND language together or neither — send-ticket-reply 400s on
          // half a pair, because silently defaulting the language is what
          // produced #132001 against a rebuilt WABA.
          ...(opts.template
            ? {
                templateName: opts.template.name,
                templateLanguage: opts.template.language,
              }
            : {}),
        },
      });

      if (error) {
        // A non-2xx response reaches supabase-js as a generic
        // FunctionsHttpError with the real JSON body left unread on
        // error.context (a Response) — same unwrap as
        // src/lib/admin-account-actions.ts's invokeAdmin.
        const ctx = (
          error as {
            context?: {
              status?: number;
              json?: () => Promise<{ error?: string; reason?: string }>;
            };
          }
        ).context;
        if (ctx?.status === 409) {
          // Already claimed/delivered — a double-invoke, this same request
          // racing itself, or a retry after the writeback below already
          // succeeded. The tenant may already have the email: never offer
          // a retry that could send a second one.
          const result: ReplySendState = { status: "sent-unconfirmed" };
          setSendState((s) => ({ ...s, [commentId]: result }));
          return result;
        }
        let message = genericFailure;
        // Task 9 — the two machine-readable WhatsApp refusals get a plain-
        // language message instead of the raw wire string surfacing
        // verbatim in a toast ("window_closed" / "empty_message" mean
        // nothing to an admin), and are marked un-retryable: nothing about
        // clicking Retry changes either condition, so retrying would only
        // fail the exact same way again.
        let retryable = true;
        if (ctx && typeof ctx.json === "function") {
          try {
            const parsed = await ctx.json();
            if (parsed.error === "window_closed") {
              message = t(
                "issueChatThread.errorWindowClosed",
                "The 24-hour WhatsApp window has closed since this was written — send a WhatsApp notification instead."
              );
              retryable = false;
              // Review round 1, finding 3 — the server's answer is
              // authoritative over the client's own (possibly stale) read.
              // Surfaces the "Send WhatsApp notification" button on the
              // very next render regardless of what whatsappWindowState
              // still believes.
              setServerWindowClosed(true);
            } else if (parsed.error === "empty_message") {
              message = t(
                "issueChatThread.errorEmptyMessage",
                "WhatsApp needs a text reply — an attachment alone can't be delivered."
              );
              retryable = false;
            } else {
              message = parsed.error || parsed.reason || genericFailure;
            }
          } catch {
            // Body wasn't JSON — keep the generic message.
          }
        }
        const result: ReplySendState = { status: "failed", message, retryable };
        setSendState((s) => ({ ...s, [commentId]: result }));
        toast.error(message);
        return result;
      }

      if (data?.sent) {
        const result: ReplySendState = {
          status: data.writebackFailed ? "sent-unconfirmed" : "sent",
          ...(data.attachmentsDropped ? { attachmentsDropped: true } : {}),
        };
        setSendState((s) => ({ ...s, [commentId]: result }));
        if (opts.isRetry) {
          toast.success(t("issueChatThread.toastMessageSent", "Message sent"));
        }
        return result;
      }

      // 200 with sent:false — portal-only ("ticket" channel) or a
      // schema-ready channel with no provider wired up (whatsapp). Not a
      // failure: the comment is already saved and visible in the thread.
      const result: ReplySendState = {
        status: "skipped",
        reason: data?.reason,
      };
      setSendState((s) => ({ ...s, [commentId]: result }));
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : genericFailure;
      const result: ReplySendState = { status: "failed", message };
      setSendState((s) => ({ ...s, [commentId]: result }));
      toast.error(message);
      return result;
    }
  };

  const postComment = useMutation({
    // Task 9 — `sendAsTemplate` is set only by the "Send WhatsApp
    // notification" button, which replaces the free-text composer once the
    // service window has closed. The template itself carries no variables
    // (WHATSAPP_TEMPLATES.reply_nudge), so there is nothing to type: the
    // empty-message guard below is the one place that must not fire for it.
    mutationFn: async (
      opts: {
        sendAsTemplate?: boolean;
        /** Picked out of Meta's approved list; absent = use the constant. */
        template?: { name: string; language: string };
        templateVariables?: string[];
        /** Template body with the admin's variables already substituted. */
        renderedBody?: string;
        /** coliviq Agent's "Send now" — sends this exact text through the
         *  same path as the normal Send button, instead of requiring the
         *  admin to copy it into the textarea first. Reading `message` state
         *  directly here would risk a stale closure if a caller set it and
         *  called mutate() in the same tick; a parameter has no such race. */
        overrideMessage?: string;
      } = {}
    ) => {
      if (!issueId)
        throw new Error(
          t("issueChatThread.errorNoIssueSelected", "No issue selected")
        );
      if (!currentUserId)
        throw new Error(t("issueChatThread.errorNotSignedIn", "Not signed in"));
      // Review finding: coliviq Agent's panel is text-only (no attachment
      // picker), so a Send now must never silently pick up whatever files
      // happen to be staged in the normal composer at that moment — the
      // admin attached those for a message they haven't sent, not for the
      // Agent's draft. `onSuccess` below likewise skips clearing them for
      // an assist send, so they survive intact for that later send.
      const isAssistSend = opts.overrideMessage !== undefined;
      const filesToSend = isAssistSend ? [] : chatFiles;
      const mentionPrefix =
        !isAssistSend && postInternal
          ? mentionIds
              .map((id) => mentionRoster.find((person) => person.id === id))
              .filter(Boolean)
              .map((person) => `@${adminDisplayName(person!, t).split(" ")[0]}`)
              .join(" ")
          : "";
      const rawMessage = opts.overrideMessage ?? message;
      const trimmed =
        `${mentionPrefix}${mentionPrefix && rawMessage.trim() ? " " : ""}${rawMessage}`.trim();
      if (!trimmed && !filesToSend.length && !opts.sendAsTemplate)
        throw new Error(
          t("issueChatThread.errorMessageEmpty", "Message is empty")
        );

      // Upload any attached files
      setUploadingFiles(true);
      const uploadedAtts: ChatAttachment[] = [];
      try {
        for (const f of filesToSend) {
          if (f.size > 10 * 1024 * 1024)
            throw new Error(
              t("issueChatThread.errorMaxSize", "{name}: max 10 MB").replace(
                "{name}",
                f.name
              )
            );
          const safeName = f.name.replace(/[^\w.\-()+\s]/g, "_");
          const path = `${issueId}/${Date.now()}-${safeName}`;
          const { error: uploadError } = await supabase.storage
            .from("support-attachments")
            .upload(path, f, {
              contentType: f.type || undefined,
              upsert: false,
            });
          if (uploadError) throw uploadError;
          uploadedAtts.push({
            name: f.name,
            type: f.type || "application/octet-stream",
            size: f.size,
            path,
            bucket: "support-attachments",
          });
        }
      } finally {
        setUploadingFiles(false);
      }

      // A template send with nothing typed still needs a real row for
      // send-ticket-reply to dispatch against (constraint: no second
      // function, no schema change — the existing claim/dispatch path
      // always operates on a comment id). The fixed marker text is what
      // shows in the thread in place of a real answer — the actual answer
      // is written once the tenant reopens the window with a new message,
      // through this same composer back in its normal, free-text state.
      // A template send records the text the tenant will RECEIVE, not a
      // sentence asserting that it was sent. The old wording was written
      // before the send was attempted, so a failure left the thread
      // permanently claiming "Sent a WhatsApp notification…" about a message
      // that never left — three identical copies of it, one per retry, in
      // the case observed on 2026-09-21. The Ban icon and delivery_error
      // carry the delivery state; the body carries the content.
      const commentText =
        trimmed || uploadedAtts.length
          ? encodeEnvelope(trimmed, uploadedAtts)
          : opts.renderedBody?.trim()
            ? opts.renderedBody.trim()
            : t(
                "issueChatThread.whatsappTemplateSentNote",
                "Sent a WhatsApp notification asking the tenant to get back in touch."
              );

      const payload: IssueCommentInsert = {
        issue_id: issueId,
        user_id: currentUserId,
        comment: commentText,
        sender_scope: senderScope,
      };

      if (internalOnly) {
        payload.is_internal = true;
      } else if (canPostInternal) {
        payload.is_internal = postInternal;
      } else {
        payload.is_internal = false;
      }

      // .select("id") captures the new row's own id — Task 10 needs it
      // immediately after, to dispatch (and, on failure, let the caller
      // retry) this exact comment, never a historical one.
      const { data: insertedRow, error } = await supabase
        .from("issue_comments")
        .insert(
          payload as Database["public"]["Tables"]["issue_comments"]["Insert"]
        )
        .select("id")
        .single();
      if (error) throw error;
      const insertedId = (insertedRow as { id: string } | null)?.id ?? null;

      const isNonInternalTenantReply =
        senderScope === "tenant" && !payload.is_internal;
      const isNonInternalAdminReply =
        senderScope === "admin" && !payload.is_internal;

      // I1 fix — dispatchReply's own outcome, threaded through this
      // mutation's return value so onSuccess can decide whether "Message
      // sent" is actually true. Stays null whenever no dispatch is even
      // attempted (internal note, tenant reply, dispatchOutbound off, or no
      // insertedId) — onSuccess treats null the same as a real success.
      let dispatchOutcome: ReplySendState | null = null;

      // Notify the assigned admin about a new tenant message (throttled
      // server-side to 1 per tenant / 10 min). Fire-and-forget — never block or
      // fail sending on the notification. Only tenant messages notify admins;
      // admin replies and internal notes do not (COL-256).
      if (isNonInternalTenantReply) {
        void supabase.functions
          .invoke("send-support-message-email", { body: { issueId } })
          .catch((e) =>
            console.warn("[IssueChatThread] admin notify failed:", e)
          );

        // I4 fix — a tenant→in_progress write used to live here
        // (`issues.update({status:"in_progress"}).eq("id",issueId)`), but
        // `issues` has no tenant UPDATE policy (only "Admins can manage all
        // issues", plus tenant INSERT/SELECT — remote_schema.sql), so it
        // always matched zero rows: PostgREST returns 204, `statusErr`
        // stays null, and nothing ever logged the silent no-op. Removed
        // rather than left as dead code that looks load-bearing; this half
        // of the brief needs a real RLS policy (a schema change, out of
        // this task's scope) before it can work at all — see the task-10
        // report's I4 entry.
      }

      // COL-324 — auto-assign on first admin response. The `.is(..., null)`
      // guard makes this first-responder-wins: it never overwrites a manual
      // assignment or an earlier responder. Fire-and-forget — replying must
      // never fail because assignment did. No system comment (noise).
      if (isNonInternalAdminReply) {
        void supabase
          .from("issues")
          .update({ assigned_to: currentUserId, assigned_by: currentUserId })
          .eq("id", issueId)
          .is("assigned_to", null)
          .then(({ error: assignErr }) => {
            if (assignErr) {
              console.warn("[IssueChatThread] auto-assign failed:", assignErr);
              return;
            }
            void queryClient.invalidateQueries({
              queryKey: ["admin-issues"],
            });
          });

        // Both the "waiting" write and the actual email dispatch below are
        // gated on `dispatchOutbound`, not just on
        // `isNonInternalAdminReply`. This shared component also renders on
        // read-only and tenant surfaces; the prop keeps delivery decisions at
        // the call site and this component reusable and testable.
        if (dispatchOutbound) {
          // Task 10 — staff replied: the ticket is now waiting on the tenant,
          // unless it's already resolved (a more final state a reply
          // shouldn't silently reopen). The `.neq` guard makes this a no-op
          // in that case instead of a read-then-write, so there's no race
          // with a concurrent manual status change. Same fire-and-forget
          // contract as auto-assign above.
          void supabase
            .from("issues")
            .update({ status: "waiting" })
            .eq("id", issueId)
            .neq("status", "resolved")
            .then(({ error: statusErr }) => {
              if (statusErr) {
                console.warn(
                  "[IssueChatThread] waiting status update failed:",
                  statusErr
                );
                return;
              }
              // Keep the established status transition compatible with an
              // environment where the snooze migration has not landed yet.
              // Clearing the optional follow-up flag is a separate best-
              // effort write: an unknown-column error here must not roll
              // back or suppress status:"waiting" above.
              void supabase
                .from("issues")
                .update({
                  follow_up_due: false,
                } as unknown as Database["public"]["Tables"]["issues"]["Update"])
                .eq("id", issueId)
                .neq("status", "resolved")
                .then(({ error: followUpErr }) => {
                  if (followUpErr) {
                    console.warn(
                      "[IssueChatThread] follow-up clear failed:",
                      followUpErr
                    );
                  }
                });
              void queryClient.invalidateQueries({
                queryKey: ["support-inbox"],
              });
              void queryClient.invalidateQueries({
                queryKey: ["support-ticket-detail", issueId],
              });
            });

          // Task 10 — actually send the reply out over the ticket's channel.
          // AWAITED (see dispatchReply's own comment for why, and for the
          // construction argument that this is always a freshly-inserted id).
          // Task 9 — `templateVariables: []` only when the composer's own
          // "Send WhatsApp notification" button was the one clicked;
          // reply_nudge takes zero variables, so `[]` (not omitted) is the
          // signal send-ticket-reply reads as "use the template".
          if (insertedId) {
            dispatchOutcome = await dispatchReply(
              insertedId,
              fromAddress,
              opts.sendAsTemplate
                ? {
                    templateVariables: opts.templateVariables ?? [],
                    ...(opts.template ? { template: opts.template } : {}),
                  }
                : {}
            );
          }
        }
      }

      return { dispatchOutcome, isAssistSend, insertedId };
    },
    onSuccess: async (result: {
      dispatchOutcome: ReplySendState | null;
      isAssistSend: boolean;
      insertedId: string | null;
    }) => {
      // Review finding: an assist send dispatches `assistReplyText`, not
      // `message` — unconditionally clearing `message`/`chatFiles` here
      // would silently discard text/attachments the admin was separately
      // composing in the normal textarea, unrelated to what was just sent.
      // Leave both untouched on an assist send so that work survives.
      if (!result.isAssistSend) {
        setMessage("");
        setMentionIds([]);
        setChatFiles([]);
      }
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ["tenant", "issues", "issue-unread"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-issues", "issue-unread"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-issues", "support-unread-total"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["tenant", "issues", "support-unread-total"],
      });
      // I1 fix — dispatchReply already toasts its own error and the bubble
      // already shows "Not sent" + Retry on a real failure; layering the
      // generic "Message sent" success toast on top of that would tell the
      // admin two contradictory things about the same reply. Every other
      // outcome (sent, sent-unconfirmed, skipped, or no dispatch attempted
      // at all) keeps the existing toast.
      if (result?.dispatchOutcome?.status !== "failed") {
        toast.success(t("issueChatThread.toastMessageSent", "Message sent"));
        if (supportAppearance && !postInternal) onOutboundSent?.();
      }
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === "object" && err !== null && "message" in err
            ? String((err as { message: unknown }).message)
            : t("issueChatThread.errorSendFailed", "Failed to send message");
      console.error("[IssueChatThread] postComment error:", err);
      toast.error(msg);
    },
  });

  /** A reply is in flight: the comment insert, its attachment upload, or a
   *  same-comment coliviq Agent retry dispatch (see isAssistRetryDispatching
   *  above). Deliberately excludes generateDraft.isPending — Codex review
   *  finding: an AI draft generation writes only to assistReplyText/
   *  assistNotice/assistError (coliviq Agent's own panel state, see
   *  generateDraft's onSuccess above), never to the normal composer's
   *  `message`/`chatFiles`, so it has nothing to protect here. Folding it in
   *  used to block the ordinary Send button (and the WhatsApp template
   *  button) for the whole generation — several seconds of a model call —
   *  even after the admin closed the panel to send an unrelated reply. */
  const isSendInFlight =
    postComment.isPending || uploadingFiles || isAssistRetryDispatching;

  // COL-322 — edit own message. Re-encodes the envelope so existing
  // attachments survive a text edit; sets edited_at so the bubble shows an
  // "(edited)" marker. RLS + the immutability trigger keep this to the
  // caller's own rows and the body/marker columns.
  const editComment = useMutation({
    mutationFn: async ({
      id,
      text,
      atts,
    }: {
      id: string;
      text: string;
      atts: ChatAttachment[];
    }) => {
      const trimmed = text.trim();
      if (!trimmed && !atts.length)
        throw new Error(
          t("issueChatThread.errorMessageEmpty", "Message is empty")
        );
      const { error } = await supabase
        .from("issue_comments")
        .update({
          comment: encodeEnvelope(trimmed, atts),
          edited_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      setEditingCommentId(null);
      setEditText("");
      await queryClient.invalidateQueries({ queryKey });
      toast.success(
        t("issueChatThread.toastMessageUpdated", "Message updated")
      );
    },
    onError: (err: unknown) => {
      console.error("[IssueChatThread] editComment error:", err);
      toast.error(
        err instanceof Error
          ? err.message
          : t(
              "issueChatThread.toastMessageUpdateFailed",
              "Failed to update message"
            )
      );
    },
  });

  // COL-322 — SOFT delete own message: the row stays (audit trail), the UI
  // renders a tombstone for everyone. Both tenants and admins go through the
  // same path — nobody hard-deletes from the thread.
  const deleteComment = useMutation({
    mutationFn: async (id: string) => {
      if (!currentUserId)
        throw new Error(t("issueChatThread.errorNotSignedIn", "Not signed in"));
      const { error } = await supabase
        .from("issue_comments")
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: currentUserId,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      setDeletingCommentId(null);
      await queryClient.invalidateQueries({ queryKey });
      toast.success(
        t("issueChatThread.toastMessageDeleted", "Message deleted")
      );
    },
    onError: (err: unknown) => {
      console.error("[IssueChatThread] deleteComment error:", err);
      toast.error(
        err instanceof Error
          ? err.message
          : t(
              "issueChatThread.toastMessageDeleteFailed",
              "Failed to delete message"
            )
      );
    },
  });

  /**
   * coliviq Agent — Send now. Extracted from inline JSX (review finding) for
   * readability; behavior otherwise unchanged except where noted below.
   *
   * A same-comment retry (unchanged draft text after a failed send) goes
   * through dispatchReply directly instead of postComment, exactly like the
   * per-message chat bubble's own "Not sent" → Retry button — see
   * dispatchReply's own comment for the server-side 409 idempotency guard
   * this leans on. An edited retry is a genuinely different message and
   * always goes through postComment as a new send.
   */
  const handleAssistSendNow = async () => {
    // Review finding (Ganesh, finding 1): defense-in-depth alongside the
    // Send-now `disabled` condition below — once the window is closed (the
    // client's own read, OR the server has already refused one send for it
    // — see `whatsappWindowClosed`'s own comment), only the pre-approved
    // template button in the normal composer can reach the tenant, never a
    // free-form reply. Senior-developer review finding: today this exact
    // function has exactly one caller (the Send-now button's `onClick`),
    // which reads the same `whatsappWindowClosed` in the same render as its
    // own `disabled` prop, and a native disabled button never dispatches a
    // click at all — so this branch is not reachable through that button
    // today. Kept anyway as a guard against a future second caller (a
    // keyboard shortcut, a programmatic call, a `disabled` condition that
    // later diverges from this check) rather than relying solely on the
    // DOM's own disabled-click suppression.
    if (whatsappWindowClosed) return;
    const trimmedText = assistReplyText.trim();
    const pendingRetry = assistFailedSendRef.current;
    const isSameCommentRetry =
      pendingRetry !== null && pendingRetry.text === trimmedText;
    try {
      // Codex review finding: the retry dispatches via dispatchReply
      // directly, which postComment.isPending never reflects — so without
      // this, isSendInFlight would leave Send now clickable for the retry's
      // own duration. A second click mid-retry could receive the server's
      // 409 ("already claimed", mapped to sent-unconfirmed) before the
      // first call's real outcome is known, closing the panel on what might
      // still turn out to be a genuine failure.
      if (isSameCommentRetry) setIsAssistRetryDispatching(true);
      const result = isSameCommentRetry
        ? {
            dispatchOutcome: await dispatchReply(
              pendingRetry!.id,
              fromAddress,
              {
                isRetry: true,
              }
            ),
            insertedId: pendingRetry!.id,
          }
        : await postComment.mutateAsync({ overrideMessage: assistReplyText });
      // Review finding: dispatchReply resolves normally even when the
      // actual email/WhatsApp delivery failed (it's a reported outcome, not
      // a thrown error — see postComment's own onSuccess, which makes the
      // identical check for its toast). Treat a failed delivery the same as
      // the catch branch below: keep the panel open with the draft intact
      // so the admin can retry, instead of silently discarding it as if the
      // message had gone out.
      if (result.dispatchOutcome?.status === "failed") {
        // Review finding: a non-retryable failure (the server's own
        // machine-readable refusal — e.g. a closed WhatsApp window) must
        // disable Send now for THIS unchanged draft, exactly like the
        // per-message bubble withholds its own Retry action for the same
        // two refusals. Editing the draft (a new comparison text) or
        // regenerating (which clears this ref entirely) is the only way
        // forward, not a repeat click on the same text.
        const retryable = result.dispatchOutcome.retryable !== false;
        if (result.insertedId) {
          assistFailedSendRef.current = {
            id: result.insertedId,
            text: trimmedText,
            retryable,
          };
        } else if (pendingRetry) {
          assistFailedSendRef.current = { ...pendingRetry, retryable };
        }
        // The failed send is an admin row, so it cannot change the inbound
        // snapshot. A concurrent tenant message still makes the draft stale.
        return;
      }
      // Review finding: without this, reopening the panel after a
      // successful send would skip regeneration (assistGeneratedSnapshotRef
      // already set) and show the already-delivered draft with an enabled
      // Send now — one more click would silently re-dispatch a duplicate
      // reply. Clearing both forces a fresh draft next time the panel
      // opens.
      assistFailedSendRef.current = null;
      setAssistOpen(false);
      setAssistIsEditing(false);
      setAssistReplyText("");
      assistGeneratedSnapshotRef.current = null;
    } catch {
      // A thrown error only reaches here from the postComment.mutateAsync
      // branch (new send) — its own onError already toasts. The
      // isSameCommentRetry branch never throws (dispatchReply catches
      // internally and toasts its own failures); either way, keep the
      // panel open with the draft intact so the admin can retry.
    } finally {
      setIsAssistRetryDispatching(false);
    }
  };

  if (!issueId) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground border rounded-lg bg-card">
        {t(
          "issueChatThread.selectTicket",
          "Select a ticket to view the conversation."
        )}
      </div>
    );
  }

  return (
    <div
      ref={threadRootRef}
      className={cn(
        "h-full flex flex-col overflow-hidden min-h-0",
        supportAppearance && "support-thread",
        hideHeader ? "" : "border rounded-lg bg-card"
      )}
    >
      {!hideHeader && (
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="text-sm font-medium">
            {t("issueChatThread.conversation", "Conversation")}
          </div>
          <div className="flex items-center gap-2">
            {canMarkUnread ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 rounded-full text-xs gap-1.5"
                onClick={() => void markTicketUnread()}
                title={t(
                  "issueChatThread.resetTicketUnreadTitle",
                  "Reset this ticket so it reappears as unread in your inbox"
                )}
              >
                <MailX className="h-3.5 w-3.5" />
                {t("issueChatThread.markUnread", "Mark unread")}
              </Button>
            ) : null}
            {showViewBadge ? (
              hideInternal ? (
                <Badge variant="secondary">
                  {t("issueChatThread.tenantView", "Tenant view")}
                </Badge>
              ) : (
                <Badge variant="outline">
                  {t("issueChatThread.adminView", "Admin view")}
                </Badge>
              )
            ) : null}
          </div>
        </div>
      )}
      {hideHeader && canMarkUnread ? (
        <div className="flex justify-end px-3 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 rounded-full text-xs gap-1.5 bg-background/80 backdrop-blur"
            onClick={() => void markTicketUnread()}
            title={t(
              "issueChatThread.resetTicketUnreadTitle",
              "Reset this ticket so it reappears as unread in your inbox"
            )}
          >
            <MailX className="h-3.5 w-3.5" />
            {t("issueChatThread.markUnread", "Mark unread")}
          </Button>
        </div>
      ) : null}

      {/* The message ground belongs to the scroll region, not to the messages.
          It used to sit on the inner content div, which is only as tall as the
          messages themselves — so a short thread painted a grey band that
          stopped mid-card with white underneath it, as if the conversation had
          run out of page. Putting it on the ScrollArea fills the whole region
          whatever the thread's length, and the dot texture now stays put while
          the messages scroll over it. Both tabs and the tenant portal render
          this same component, so they all get it. */}
      <ScrollArea
        className="flex-1 min-h-0 chat-scroll"
        type="always"
        style={{
          background: "#EFF2F3",
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(53,207,200,0.06) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      >
        <div
          className={
            supportAppearance
              ? "support-messages relative"
              : "p-4 sm:p-5 pr-6 space-y-1.5 relative"
          }
        >
          {supportAppearance && channel === "email" && ticket?.title ? (
            <div className="support-subject" title={ticket.title}>
              <span>{t("support.new.subject", "Subject")}</span>
              <strong>{ticket.title}</strong>
            </div>
          ) : null}
          {ticket?.description ? (
            <div
              className={
                supportAppearance
                  ? "support-founding-message flex flex-col items-start gap-1.5 pb-2"
                  : "flex justify-center pb-2"
              }
            >
              <div
                className={
                  supportAppearance
                    ? "support-founding-bubble rounded-2xl rounded-bl-md bg-[#F4F3EF]"
                    : "max-w-[92%] sm:max-w-[80%] rounded-2xl bg-amber-50/90 px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-amber-200/50"
                }
              >
                <div
                  className={
                    supportAppearance
                      ? "hidden"
                      : "text-[11px] font-semibold tracking-wide text-amber-700/80 uppercase"
                  }
                >
                  {t("issueChatThread.ticketDescription", "Ticket description")}
                </div>
                {/* The founding message's body, which for an inbound email
                    with attachments is a CHATENV envelope — the same shape a
                    comment carries. Rendering it raw showed the tenant's
                    ticket as a wall of JSON (caught on prod 2026-09-29); the
                    comment path has always decoded, the description never
                    did, because until inbound email attachments existed no
                    envelope could reach it. */}
                {(() => {
                  const { text: descText, atts: descAtts } = decodeEnvelope(
                    showOriginalDescription && originalDescription
                      ? originalDescription
                      : (ticket.description ?? "")
                  );
                  return (
                    <>
                      {descText ? (
                        <div className="mt-1 text-sm text-foreground/90 whitespace-pre-wrap break-words leading-relaxed">
                          {descText}
                        </div>
                      ) : null}
                      {descAtts.length ? (
                        <div className="mt-2 flex flex-col gap-1.5">
                          {descAtts.map((att, ai) => (
                            <ChatAttachmentBubble key={ai} att={att} />
                          ))}
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </div>
              {supportAppearance && ticket.created_at && (
                <div className="support-message-meta">
                  {channel === "whatsapp"
                    ? "WhatsApp"
                    : t("support.channel.email", "Email")}{" "}
                  · {formatDayPill(ticket.created_at, t, baseLang)},{" "}
                  {formatTime(ticket.created_at, baseLang)}
                  {originalDescription &&
                  originalDescription !== ticket.description ? (
                    <>
                      {" · "}
                      <span>
                        {showOriginalDescription
                          ? t(
                              "support.translation.showingOriginal",
                              "Showing original"
                            )
                          : translatedFrom
                            ? t(
                                "support.translation.translatedFrom",
                                "Translated from {language}"
                              ).replace("{language}", translatedFrom)
                            : t("support.email.cleaned", "Cleaned email")}
                      </span>
                      {" · "}
                      <button
                        type="button"
                        className="support-translation-toggle"
                        onClick={() =>
                          setShowOriginalDescription((value) => !value)
                        }
                      >
                        {showOriginalDescription
                          ? translatedFrom
                            ? t(
                                "support.translation.showTranslation",
                                "Show translation"
                              )
                            : t(
                                "support.email.showCleaned",
                                "Show cleaned email"
                              )
                          : t(
                              "support.translation.showOriginal",
                              "Show original"
                            )}
                      </button>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}

          {attachments.length ? (
            <div className="rounded-2xl border border-border/60 bg-background p-3">
              <div className="text-xs font-semibold text-muted-foreground">
                {t("issueChatThread.attachments", "Attachments")}
              </div>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {attachments.map((a) => (
                  <TicketAttachmentTile key={a.file_path} a={a} />
                ))}
              </div>
            </div>
          ) : null}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="flex items-center gap-2 rounded-full bg-background/80 px-4 py-2 text-sm text-muted-foreground shadow-sm">
                <span className="h-2 w-2 rounded-full bg-[#35CFC8] animate-bounce [animation-delay:-0.3s]" />
                <span className="h-2 w-2 rounded-full bg-[#35CFC8] animate-bounce [animation-delay:-0.15s]" />
                <span className="h-2 w-2 rounded-full bg-[#35CFC8] animate-bounce" />
              </div>
            </div>
          ) : comments && comments.length ? (
            comments.map((c, idx) => {
              const scope = (c.sender_scope ?? null) as
                | "tenant"
                | "admin"
                | null;
              const isMine = c.user_id === currentUserId;
              const alignRight = scope ? scope === senderScope : isMine;
              const internal = !!c.is_internal;
              // Staff bubble on a staff surface: tag it with the author, not a
              // generic ADMIN label, so a shared inbox shows who answered and
              // who left each internal note. Shows adminLabel until profiles
              // resolve (no flash of the stand-in), then adminDisplayName's own
              // name → email → stand-in chain.
              const showAuthor = scope === "admin" && senderScope === "admin";
              // The other party's label, when this surface didn't name one.
              // `supportLabel` ("SUPPORT") is only ever right on the TENANT
              // portal, where staff are support. On a staff surface the
              // incoming side is the tenant, and falling back to "SUPPORT"
              // there labels the tenant's own message as though support wrote
              // it. Keyed on senderScope so a surface
              // that forgets the prop is right by default rather than wrong.
              const otherPartyLabel =
                senderScope === "admin"
                  ? tenantLabel || t("issueChatThread.tenant", "TENANT")
                  : supportLabel;
              const who =
                scope === "admin"
                  ? showAuthor && staffAuthors
                    ? adminDisplayName(staffAuthors[c.user_id], t)
                    : adminLabel
                  : alignRight
                    ? youLabel
                    : otherPartyLabel;

              const prev = idx > 0 ? comments[idx - 1] : null;
              const next = idx < comments.length - 1 ? comments[idx + 1] : null;

              const prevScope = (prev?.sender_scope ?? null) as
                | "tenant"
                | "admin"
                | null;
              const nextScope = (next?.sender_scope ?? null) as
                | "tenant"
                | "admin"
                | null;
              const prevAlignRight = prev
                ? prevScope
                  ? prevScope === senderScope
                  : prev.user_id === currentUserId
                : null;
              const nextAlignRight = next
                ? nextScope
                  ? nextScope === senderScope
                  : next.user_id === currentUserId
                : null;

              const showDaySeparator =
                !prev ||
                (prev.created_at &&
                  c.created_at &&
                  new Date(prev.created_at).toDateString() !==
                    new Date(c.created_at).toDateString());

              // A streak is one author's run. The label above only renders on
              // a streak's first bubble, so two admins replying within the
              // 3-minute window must not collapse under the first one's name.
              const sameAsPrev =
                !showDaySeparator &&
                prev &&
                prev.user_id === c.user_id &&
                prevAlignRight === alignRight &&
                Math.abs(
                  Date.parse(c.created_at) - Date.parse(prev.created_at)
                ) <
                  3 * 60 * 1000;
              const sameAsNext =
                next &&
                next.user_id === c.user_id &&
                !(
                  next.created_at &&
                  c.created_at &&
                  new Date(next.created_at).toDateString() !==
                    new Date(c.created_at).toDateString()
                ) &&
                nextAlignRight === alignRight &&
                Math.abs(
                  Date.parse(next.created_at) - Date.parse(c.created_at)
                ) <
                  3 * 60 * 1000;

              const isFirstOfStreak = !sameAsPrev;
              const isLastOfStreak = !sameAsNext;

              const bubbleRadius = alignRight
                ? cn(
                    "rounded-2xl",
                    isLastOfStreak ? "rounded-br-md" : "rounded-br-2xl",
                    !isFirstOfStreak && "rounded-tr-md"
                  )
                : cn(
                    "rounded-2xl",
                    isLastOfStreak ? "rounded-bl-md" : "rounded-bl-2xl",
                    !isFirstOfStreak && "rounded-tl-md"
                  );

              const { text: storedText, atts: msgAtts } = decodeEnvelope(
                c.comment
              );
              const activityText = supportSystemEventText(
                storedText,
                c.sender_scope,
                c.delivered_at,
                c.is_system,
                t
              );
              // The legacy ticket view still renders these rows as bubbles;
              // strip the storage marker there too so it never reaches users.
              const msgText = activityText ?? storedText;
              const systemEvent =
                supportAppearance && !internal ? activityText : null;

              // COL-322 — soft-deleted messages render as a tombstone for
              // everyone; edit/delete are only offered on the caller's own,
              // not-yet-deleted messages (both scopes).
              const isDeleted = !!c.deleted_at;
              // A message that went out (or came in) by email already sits in
              // someone else's mailbox: editing the row here would only fork
              // it from what the tenant actually received, and the affordance
              // used to imply otherwise. Gated on the row, not the ticket's
              // channel — one ticket can hold both portal and email messages.
              // `delivered_at` alone also covers synthetic system notes that
              // are deliberately never sent to a customer.
              const isDelivered = !!(
                c.email_message_id ||
                c.provider_message_id ||
                c.delivered_at
              );
              const canModify =
                !supportAppearance &&
                !readOnly &&
                isMine &&
                !isDeleted &&
                !isDelivered;
              // Explain the withheld Edit/Delete exactly where they would
              // otherwise have appeared: the caller's own, live, delivered row.
              const lockedHint =
                !readOnly && isMine && !isDeleted && isDelivered
                  ? t(
                      "issueChatThread.deliveredLocked",
                      "Already delivered — can't be edited or deleted"
                    )
                  : undefined;
              const isEditing = editingCommentId === c.id;
              // "Mark as unread" stays available on every message (including
              // tombstones); Edit/Delete only where canModify allows.
              const showMenu =
                !supportAppearance &&
                (canMarkUnread || canModify) &&
                !isEditing;

              if (systemEvent) {
                return (
                  <div
                    className="support-system-event"
                    data-comment-id={c.id}
                    key={c.id}
                  >
                    <Check aria-hidden size={13} />
                    <span>{systemEvent}</span>
                    <time>
                      · {formatDayPill(c.created_at, t, baseLang)},{" "}
                      {formatTime(c.created_at, baseLang)}
                    </time>
                  </div>
                );
              }

              return (
                <div key={c.id} data-comment-id={c.id}>
                  {showDaySeparator && !supportAppearance && (
                    <div className="flex items-center justify-center my-3">
                      <div className="rounded-full bg-background/85 backdrop-blur px-3 py-1 text-[11px] font-medium text-muted-foreground shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
                        {formatDayPill(c.created_at, t, baseLang)}
                      </div>
                    </div>
                  )}
                  <div
                    className={cn(
                      "group/msg flex animate-in fade-in-0 slide-in-from-bottom-1 duration-200",
                      alignRight ? "justify-end" : "justify-start",
                      isLastOfStreak ? "mb-2" : "mb-0.5"
                    )}
                  >
                    <div
                      className={cn(
                        supportAppearance
                          ? "support-message-group flex flex-col"
                          : "max-w-[85%] sm:max-w-[72%] flex flex-col",
                        alignRight ? "items-end" : "items-start"
                      )}
                    >
                      {!supportAppearance &&
                      isFirstOfStreak &&
                      (!alignRight || showAuthor) ? (
                        <div className="text-[11px] font-semibold tracking-wide mb-1 px-2 text-[#0f766e]/80">
                          {who}
                        </div>
                      ) : null}
                      <div className="relative flex items-center gap-1">
                        {showMenu && alignRight ? (
                          <div className="opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 transition-opacity">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 rounded-full text-muted-foreground hover:text-foreground"
                                  aria-label={t(
                                    "issueChatThread.messageActions",
                                    "Message actions"
                                  )}
                                >
                                  <MoreVertical className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                {canMarkUnread ? (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      void markUnreadFrom(c.created_at)
                                    }
                                    className="cursor-pointer"
                                  >
                                    <MailX className="h-3.5 w-3.5 mr-2" />
                                    {t(
                                      "issueChatThread.markAsUnread",
                                      "Mark as unread"
                                    )}
                                  </DropdownMenuItem>
                                ) : null}
                                {canModify ? (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setEditingCommentId(c.id);
                                        setEditText(msgText);
                                      }}
                                      className="cursor-pointer"
                                    >
                                      <Pencil className="h-3.5 w-3.5 mr-2" />
                                      {t("issueChatThread.edit", "Edit")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setDeletingCommentId(c.id)}
                                      className="cursor-pointer text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                                      {t("issueChatThread.delete", "Delete")}
                                    </DropdownMenuItem>
                                  </>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : null}
                        <div
                          title={lockedHint}
                          data-support-bubble={
                            supportAppearance
                              ? internal
                                ? "note"
                                : alignRight
                                  ? "outgoing"
                                  : "incoming"
                              : undefined
                          }
                          className={cn(
                            "px-3 py-2 shadow-[0_1px_1px_rgba(0,0,0,0.08)] transition-shadow",
                            bubbleRadius,
                            alignRight
                              ? "bg-[#DCFCE7] text-foreground"
                              : "bg-white text-foreground",
                            internal &&
                              !isDeleted &&
                              "ring-2 ring-amber-300/70",
                            isDeleted && "opacity-70"
                          )}
                        >
                          {isDeleted ? (
                            <div className="flex items-center gap-1.5 text-sm italic text-muted-foreground">
                              <Ban className="h-3.5 w-3.5 shrink-0" />
                              {t(
                                "issueChatThread.messageDeleted",
                                "Message deleted"
                              )}
                            </div>
                          ) : isEditing ? (
                            <div className="w-[280px] max-w-full">
                              <Textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                rows={3}
                                autoFocus
                                aria-label={t(
                                  "issueChatThread.editMessageLabel",
                                  "Edit message"
                                )}
                                className="text-sm bg-background resize-none"
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") {
                                    e.stopPropagation();
                                    setEditingCommentId(null);
                                    setEditText("");
                                  }
                                }}
                              />
                              <div className="mt-1.5 flex items-center justify-end gap-1.5">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 rounded-full text-xs"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditText("");
                                  }}
                                >
                                  {t("issueChatThread.cancel", "Cancel")}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-7 rounded-full text-xs bg-[#35CFC8] hover:bg-[#2fbdb7] text-white"
                                  disabled={
                                    editComment.isPending ||
                                    (!editText.trim() && !msgAtts.length)
                                  }
                                  onClick={() =>
                                    editComment.mutate({
                                      id: c.id,
                                      text: editText,
                                      atts: msgAtts,
                                    })
                                  }
                                >
                                  {t("issueChatThread.save", "Save")}
                                </Button>
                              </div>
                            </div>
                          ) : msgText ? (
                            <div className="text-sm whitespace-pre-wrap break-words leading-snug">
                              {msgText}
                            </div>
                          ) : null}
                          {!isDeleted && !isEditing && msgAtts.length > 0 && (
                            <div
                              className={cn(
                                "flex flex-col gap-1.5",
                                msgText ? "mt-2" : ""
                              )}
                            >
                              {msgAtts.map((att, ai) => (
                                <ChatAttachmentBubble key={ai} att={att} />
                              ))}
                            </div>
                          )}
                          <div
                            className={
                              supportAppearance
                                ? "support-message-meta support-bubble-meta"
                                : "mt-0.5 flex items-center justify-end gap-1.5"
                            }
                          >
                            {supportAppearance && (
                              <span>
                                {alignRight ? `${who} · ` : ""}
                                {internal
                                  ? t(
                                      "issueChatThread.internalNote",
                                      "Internal note"
                                    )
                                  : channel === "whatsapp"
                                    ? "WhatsApp"
                                    : t("support.channel.email", "Email")}{" "}
                                · {formatDayPill(c.created_at, t, baseLang)},
                              </span>
                            )}
                            {internal && !isDeleted ? (
                              <Badge
                                variant="secondary"
                                className="h-4 px-1.5 text-[10px] bg-amber-100 text-amber-800 border-0"
                              >
                                {t("issueChatThread.internal", "Internal")}
                              </Badge>
                            ) : null}
                            {c.edited_at && !isDeleted ? (
                              <span className="text-[10px] italic text-muted-foreground">
                                ({t("issueChatThread.edited", "edited")})
                              </span>
                            ) : null}
                            <span className="text-[10px] tabular-nums text-muted-foreground">
                              {formatTime(c.created_at, baseLang)}
                            </span>
                            {alignRight && !isDeleted ? (
                              // A row carrying delivery_error did NOT reach
                              // the tenant — either the dispatch failed, or
                              // Meta accepted it and a later `failed` receipt
                              // retracted the delivery. Showing it the same
                              // double-tick as a real delivery is the one
                              // outcome staff must never see, so the tick is
                              // swapped rather than annotated. The live
                              // per-send feedback below (sendState) is
                              // unchanged and still owns the retry affordance.
                              c.delivery_error ? (
                                // The provider's own reason rides along on the
                                // marker. Without it the icon says only "not
                                // delivered", which reads as a bug in the
                                // inbox — a WhatsApp template rejected with
                                // "Business eligibility payment issue" (an
                                // unpaid WABA, nothing to do with the code)
                                // was reported that way on 2026-09-30.
                                <span
                                  title={`${t(
                                    "issueChatThread.deliveryFailed",
                                    "Not delivered"
                                  )}: ${c.delivery_error}`}
                                  className="inline-flex"
                                >
                                  <Ban
                                    className="h-3.5 w-3.5 text-destructive"
                                    aria-label={`${t(
                                      "issueChatThread.deliveryFailed",
                                      "Not delivered"
                                    )}: ${c.delivery_error}`}
                                  />
                                </span>
                              ) : c.read_at ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-[#0f766e]">
                                  <CheckCheck className="h-3.5 w-3.5" />
                                  {t("issueChatThread.read", "Read")}
                                </span>
                              ) : !channelProducesDeliveryReceipt(channel) ||
                                c.delivered_at ? (
                                <CheckCheck
                                  className="h-3.5 w-3.5 text-[#35CFC8]"
                                  aria-label={t(
                                    "issueChatThread.delivered",
                                    "Delivered"
                                  )}
                                />
                              ) : (
                                // Neither stamp set on a channel that DOES
                                // produce receipts: the dispatch never ran, or
                                // never reported back. The row is not
                                // delivered, and the double-tick used to claim
                                // it was — persistently, because sendState
                                // below is in-memory and gone after a reload.
                                // This is exactly the state prod sat in while
                                // send-ticket-reply 404'd (PR #1595): comments
                                // saved, nothing dispatched, every one of them
                                // showing delivered.
                                <Check
                                  className="h-3.5 w-3.5 text-muted-foreground"
                                  aria-label={t(
                                    "issueChatThread.sentNotConfirmed",
                                    "Sent — delivery not confirmed"
                                  )}
                                />
                              )
                            ) : null}
                          </div>
                          {/* Task 10 — reply-dispatch feedback. sendState is only
                              ever keyed by an id this component itself just
                              inserted (see dispatchReply's comment), so this can
                              never render for a historical or system-authored row. */}
                          {sendState[c.id]?.status === "sending" ? (
                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                              {t("issueChatThread.sending", "Sending…")}
                            </div>
                          ) : sendState[c.id]?.status === "failed" ? (
                            <div className="mt-0.5 flex items-center justify-end gap-1.5 text-[10px] text-destructive">
                              {/* Every other failure (network blip, Resend/
                                  Graph API error) keeps the existing generic
                                  "Not sent" label — the specific message
                                  still reaches the admin via the toast at
                                  the moment of failure (unchanged). Only the
                                  two un-retryable, machine-readable WhatsApp
                                  refusals get their plain-language message
                                  inline too, since that toast will have long
                                  since disappeared by the time anyone reads
                                  a bubble that can never be retried as-is. */}
                              <span>
                                {(sendState[c.id] as { retryable?: boolean })
                                  ?.retryable === false
                                  ? (sendState[c.id] as { message?: string })
                                      ?.message
                                  : t("issueChatThread.notSent", "Not sent")}
                              </span>
                              {(sendState[c.id] as { retryable?: boolean })
                                ?.retryable !== false ? (
                                <button
                                  type="button"
                                  className="font-medium underline underline-offset-2 hover:no-underline"
                                  onClick={() =>
                                    void dispatchReply(c.id, fromAddress, {
                                      isRetry: true,
                                    })
                                  }
                                >
                                  {t("issueChatThread.retry", "Retry")}
                                </button>
                              ) : null}
                            </div>
                          ) : sendState[c.id]?.status === "sent-unconfirmed" ? (
                            <div className="mt-0.5 text-[10px] italic text-muted-foreground">
                              {t(
                                "issueChatThread.sentUnconfirmed",
                                "Sent, but we couldn't confirm it — check the ticket."
                              )}
                            </div>
                          ) : sendState[c.id]?.status === "sent" &&
                            (
                              sendState[c.id] as {
                                attachmentsDropped?: boolean;
                              }
                            ).attachmentsDropped ? (
                            // Beyond-the-brief item 1 — the reply's text went
                            // out, but at least one attached file did not.
                            // WhatsApp now carries one file per reply (as the
                            // message itself, with the text as its caption),
                            // so this fires for the residue: a second file, or
                            // one Meta refused for size or type. The wording
                            // deliberately no longer says "text only" — the
                            // first file may well have gone.
                            <div className="mt-0.5 text-[10px] text-amber-700">
                              {t(
                                "issueChatThread.whatsappSomeAttachmentsDropped",
                                "Some attachments weren't sent over WhatsApp."
                              )}
                            </div>
                          ) : isUndeliveredSkip(sendState[c.id]) ? (
                            // I2 fix — portal-only ("ticket" channel,
                            // nothing to send out) stays silent on purpose;
                            // only a genuinely-unsupported channel
                            // (whatsapp today) renders anything here, so it
                            // doesn't look identical to a normal send.
                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                              {t(
                                "issueChatThread.notDelivered",
                                "Not delivered — this channel isn't connected yet."
                              )}
                            </div>
                          ) : null}
                        </div>
                        {showMenu && !alignRight ? (
                          <div className="opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 transition-opacity">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 rounded-full text-muted-foreground hover:text-foreground"
                                  aria-label={t(
                                    "issueChatThread.messageActions",
                                    "Message actions"
                                  )}
                                >
                                  <MoreVertical className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="start"
                                className="w-44"
                              >
                                {canMarkUnread ? (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      void markUnreadFrom(c.created_at)
                                    }
                                    className="cursor-pointer"
                                  >
                                    <MailX className="h-3.5 w-3.5 mr-2" />
                                    {t(
                                      "issueChatThread.markAsUnread",
                                      "Mark as unread"
                                    )}
                                  </DropdownMenuItem>
                                ) : null}
                                {canModify ? (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setEditingCommentId(c.id);
                                        setEditText(msgText);
                                      }}
                                      className="cursor-pointer"
                                    >
                                      <Pencil className="h-3.5 w-3.5 mr-2" />
                                      {t("issueChatThread.edit", "Edit")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setDeletingCommentId(c.id)}
                                      className="cursor-pointer text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                                      {t("issueChatThread.delete", "Delete")}
                                    </DropdownMenuItem>
                                  </>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : supportAppearance && ticket?.description ? null : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-14 w-14 rounded-2xl bg-[#35CFC8]/15 flex items-center justify-center mb-3">
                <Smile className="h-7 w-7 text-[#0f766e]" />
              </div>
              <div className="text-sm font-semibold text-foreground">
                {t("issueChatThread.noMessagesYet", "No messages yet")}
              </div>
              <div className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                {t(
                  "issueChatThread.startTheConversation",
                  "Start the conversation — your first message will land right here."
                )}
              </div>
            </div>
          )}
          {conversationFooter}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {readOnly ? null : (
        <div
          className={cn(
            "border-t border-border/60 shrink-0 px-3 py-3 sm:px-4",
            supportAppearance && "support-composer"
          )}
          style={{ background: "#F7F8FA" }}
        >
          {/* Review finding: relocated out of the free-text composer's
              !whatsappWindowClosed block (2026-10-17) — a failed Send now
              whose server response reports window_closed flips
              whatsappWindowClosed to true (see serverWindowClosed's own
              comment), which used to unmount this ENTIRE panel along with
              the free-text composer it was nested inside, hiding the very
              draft the admin was trying to send. Docked here instead, as a
              footer-level sibling above whichever composer variant (free-
              text or the template picker) is currently showing, so the
              panel and its draft survive a window closing mid-session. The
              sparkle TOGGLE button that opens it is unchanged — still only
              reachable while the free-text composer itself is, so the
              Agent can't be newly opened on an already-closed window. */}
          {canUseAiAssist && assistOpen && (
            // Docked above the composer, inside the same column as
            // the conversation — never an overlay/modal, so the
            // thread above stays visible. Bounded height + its own
            // scroll region (not the panel growing unbounded) is
            // what keeps a long draft from either clipping or
            // pushing the page into scrolling — verified at
            // 1440x900, 1280x720, and 390px.
            <div
              id="coliviq-agent-panel"
              className="mb-1 flex max-h-[45vh] flex-col overflow-hidden rounded-xl border border-[#35CFC8]/60 bg-white shadow-sm"
            >
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                  <Sparkles className="h-4 w-4 shrink-0 text-[#0f766e]" />
                  <span className="truncate">
                    {t("issueChatThread.assistTitle", "coliviq Agent")}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t(
                    "issueChatThread.assistClose",
                    "Close coliviq Agent"
                  )}
                  className="h-7 w-7 shrink-0"
                  onClick={() => {
                    setAssistOpen(false);
                    setAssistIsEditing(false);
                  }}
                >
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              </div>

              {assistIsStale && (
                <p
                  role="status"
                  className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
                >
                  {t(
                    "issueChatThread.assistStale",
                    "A new message has arrived — close and reopen to refresh this reply."
                  )}
                </p>
              )}

              {/* Review finding (Ganesh, finding 1): reuses the exact
                  explainer text the normal composer already shows for a
                  closed WhatsApp window (see the whatsappWindowClosed block
                  further down) — no second template/sending flow lives
                  here, just guidance pointing at the one that already does.
                  The draft itself stays visible below so the admin doesn't
                  lose it. */}
              {whatsappWindowClosed && (
                <p
                  role="status"
                  className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
                >
                  {t(
                    "issueChatThread.whatsappWindowClosedExplainer",
                    "It's been over 24 hours since the tenant last wrote on WhatsApp, so Meta only allows a pre-approved notification, not a free-form reply. Send one to ask them to get back in touch — you'll be able to write your answer once they do."
                  )}
                </p>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2.5">
                {generateDraft.isPending ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("issueChatThread.assistGenerating", "Generating reply…")}
                  </div>
                ) : assistError ? (
                  // Review finding (Ganesh): a technical generation failure
                  // (network/model error, not a no_reply recommendation —
                  // see assistNotice below, which intentionally gets no
                  // retry) used to leave the admin stuck closing and
                  // reopening the panel. Reuses startDraftGeneration, so the
                  // retry gets the same request-epoch and failed-send-reset
                  // protections as the initial auto-generate.
                  <div className="space-y-2">
                    <p role="alert" className="text-xs text-destructive">
                      {assistError}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7"
                      disabled={generateDraft.isPending}
                      onClick={() =>
                        startDraftGeneration(currentConversationSnapshot)
                      }
                    >
                      {t("issueChatThread.assistRetry", "Retry")}
                    </Button>
                  </div>
                ) : assistNotice ? (
                  <p
                    role="status"
                    className="text-xs font-medium text-amber-700"
                  >
                    {assistNotice}
                  </p>
                ) : assistIsEditing ? (
                  <Textarea
                    value={assistEditDraft}
                    onChange={(e) => setAssistEditDraft(e.target.value)}
                    aria-label={t(
                      "issueChatThread.assistEditLabel",
                      "Edit draft reply"
                    )}
                    className="min-h-[120px] resize-none bg-background text-sm"
                    autoFocus
                  />
                ) : (
                  <p className="whitespace-pre-wrap text-sm text-foreground">
                    {assistReplyText}
                  </p>
                )}
              </div>

              {!generateDraft.isPending && !assistError && !assistNotice && (
                <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 px-3 py-2">
                  {assistIsEditing ? (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-[#0f766e] text-white hover:bg-[#115e59]"
                      onClick={() => {
                        // Codex review finding: do NOT unconditionally clear
                        // assistFailedSendRef here. Saving back to the exact
                        // same (trimmed) text — e.g. the admin opened Edit
                        // and changed nothing — must stay a retry of the
                        // SAME comment; wiping the ref would make the next
                        // Send now insert and dispatch a brand-new comment
                        // instead, bypassing the server's per-comment
                        // idempotency guard and risking a genuine duplicate
                        // reply if the original delivery actually succeeded
                        // and only its response was lost. Saving to
                        // genuinely DIFFERENT text already produces the
                        // right behavior on its own: handleAssistSendNow's
                        // own text comparison (isSameCommentRetry) and the
                        // Send-now disabled condition both key off text
                        // equality against this ref, so a real edit already
                        // routes through postComment as a new send and is
                        // never blocked by a stale non-retryable flag —
                        // nothing needs clearing here at all.
                        setAssistReplyText(assistEditDraft);
                        setAssistIsEditing(false);
                      }}
                    >
                      <Check className="mr-1.5 h-3.5 w-3.5" />
                      {t("issueChatThread.assistSave", "Save")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => {
                          setAssistEditDraft(assistReplyText);
                          setAssistIsEditing(true);
                        }}
                      >
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        {t("issueChatThread.assistEdit", "Edit")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 bg-[#35CFC8] text-white hover:bg-[#2fbdb7]"
                        disabled={
                          isSendInFlight ||
                          !assistReplyText.trim() ||
                          // Review finding (Ganesh, finding 1): a closed
                          // WhatsApp window (the client's own read, OR the
                          // server has already refused one send for it) can
                          // only be answered via the pre-approved template
                          // button below, never a free-form reply — see
                          // handleAssistSendNow's own defense-in-depth guard
                          // for the same condition. Always false for
                          // email/ticket (whatsappWindowClosed only derives
                          // true when channel === "whatsapp").
                          whatsappWindowClosed ||
                          // Review finding: a stale draft (a new
                          // message arrived while this one was
                          // generated) must be regenerated before
                          // it can be sent — the staleness notice
                          // above explains why; Close+reopen
                          // already regenerates (see the
                          // assistGeneratedSnapshotRef effect).
                          assistIsStale ||
                          // Review finding: a non-retryable failed
                          // send (e.g. a closed WhatsApp window)
                          // must stay disabled for THIS unchanged
                          // draft, exactly like the per-message
                          // chat bubble withholds its own Retry
                          // action for the same refusals — editing
                          // or regenerating is the only way
                          // forward, not a repeat click.
                          (assistFailedSendRef.current !== null &&
                            assistFailedSendRef.current.text ===
                              assistReplyText.trim() &&
                            assistFailedSendRef.current.retryable === false)
                        }
                        onClick={handleAssistSendNow}
                      >
                        <Send className="mr-1.5 h-3.5 w-3.5" />
                        {t("issueChatThread.assistSendNow", "Send now")}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Task 10 — FROM picker. Internal notes never email anyone, so
              this is gated on !internalOnly too, not just the channel.
              `fromAddress` stays null until the admin actively picks one —
              the visible value here falls back to SEND_AS_IDENTITIES[0]
              only for display; see the fromAddress state comment above for
              why an untouched picker still sends no override. */}
          {!supportAppearance &&
            !internalOnly &&
            !(supportAppearance && postInternal) &&
            channel === "email" && (
              <div className="flex items-center gap-2 pb-2">
                <span className="text-xs text-muted-foreground">
                  {t("issueChatThread.sendAsLabel", "Send as")}
                </span>
                <Select
                  value={fromAddress ?? SEND_AS_IDENTITIES[0]}
                  onValueChange={(v) => setFromAddress(v as SendAsIdentity)}
                >
                  <SelectTrigger
                    className="h-7 w-auto min-w-[180px] text-xs"
                    aria-label={t("issueChatThread.sendAsLabel", "Send as")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEND_AS_IDENTITIES.map((addr) => (
                      <SelectItem key={addr} value={addr} className="text-xs">
                        {addr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          {/* Task 9 — WhatsApp service window. Mirrors the FROM picker's own
              gating (!internalOnly, channel-specific) plus dispatchOutbound
              (see whatsappWindowState's own comment above for why). Meta
              accepts a free-form message only within 24h of the tenant's
              last one; outside it only the pre-approved, content-free
              reply_nudge template can go out, so once closed the free-text
              composer below is replaced entirely by the button here —
              nobody should type a reply that cannot be delivered as typed. */}
          {/* Gated on !whatsappWindowClosed too, not just the client's own
              whatsappWindowState.open: once the server has said
              window_closed, that combined flag flips even if the client's
              own (by-then-stale) read still thinks it's open — showing
              "closes in Xh" right next to "the window has closed" would be
              actively contradictory. */}
          {whatsappWindowState?.open && !whatsappWindowClosed && (
            <div className="pb-2 text-xs text-muted-foreground">
              {t(
                "issueChatThread.whatsappWindowRemaining",
                "WhatsApp reply window closes {time}"
              ).replace(
                "{time}",
                relativeTime(
                  whatsappWindowState.expiresAt,
                  language === "de" ? "de-DE" : undefined
                )
              )}
            </div>
          )}

          {whatsappWindowClosed && (
            <div className="mb-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5">
              <p className="text-xs text-amber-900">
                {t(
                  "issueChatThread.whatsappWindowClosedExplainer",
                  "It's been over 24 hours since the tenant last wrote on WhatsApp, so Meta only allows a pre-approved notification, not a free-form reply. Send one to ask them to get back in touch — you'll be able to write your answer once they do."
                )}
              </p>
              {/* Three states, deliberately distinct. `unavailable` must not
                  render as "no templates": that would send an admin to Meta's
                  console to create something that already exists. */}
              {templatesQuery.isLoading ? (
                <div className="mt-2 flex items-center gap-2 text-xs text-amber-900">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t(
                    "issueChatThread.templatesLoading",
                    "Loading approved templates…"
                  )}
                </div>
              ) : !templatesUnavailable && templates.length === 0 ? (
                <p className="mt-2 text-xs text-amber-900">
                  {t(
                    "issueChatThread.supportTemplateMissing",
                    "The approved support notification is unavailable. Approve support_reply_nudge in Meta's WhatsApp Manager to contact this tenant."
                  )}
                </p>
              ) : templatesUnavailable ? (
                // Fallback: the constant, exactly as before the picker existed.
                <Button
                  type="button"
                  size="sm"
                  className="mt-2 h-7 rounded-full text-xs bg-[#35CFC8] hover:bg-[#2fbdb7] text-white"
                  disabled={isSendInFlight || !currentUserId}
                  onClick={() => postComment.mutate({ sendAsTemplate: true })}
                >
                  {isSendInFlight ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t(
                      "issueChatThread.sendWhatsappTemplate",
                      "Send WhatsApp notification"
                    )
                  )}
                </Button>
              ) : (
                <div className="mt-2 space-y-2">
                  {/* Every approved template; the preview below shows the
                      message staff are about to send. */}
                  <div
                    role="radiogroup"
                    aria-label={t(
                      "issueChatThread.templatePick",
                      "Choose a notification"
                    )}
                    className="-mx-0.5 flex gap-2 overflow-x-auto px-0.5 pb-1"
                  >
                    {templates.map((tpl) => {
                      const isPicked =
                        !!selectedTemplate &&
                        templateKey(selectedTemplate) === templateKey(tpl);
                      return (
                        <button
                          key={templateKey(tpl)}
                          type="button"
                          role="radio"
                          aria-checked={isPicked}
                          onClick={() =>
                            setSelectedTemplateKey(templateKey(tpl))
                          }
                          className={cn(
                            "w-52 shrink-0 rounded-xl border p-2 text-left transition",
                            isPicked
                              ? "border-[#35CFC8] bg-white ring-1 ring-[#35CFC8]"
                              : "border-amber-200 bg-white/60 hover:bg-white"
                          )}
                        >
                          <span className="block truncate text-[11px] font-medium text-amber-950">
                            {tpl.name}
                          </span>
                          <span className="block text-[10px] uppercase tracking-wide text-amber-700/70">
                            {tpl.language}
                            {tpl.category ? ` · ${tpl.category}` : ""}
                          </span>
                          {/* The body, verbatim with {{n}} intact. The
                              substituted preview below is what changes as the
                              admin types; this stays the template's identity. */}
                          <span className="mt-1 line-clamp-3 block whitespace-pre-wrap text-[11px] leading-snug text-amber-900">
                            {tpl.bodyText}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {selectedTemplate ? (
                    <>
                      {/* One input per positional parameter. Labelled by
                          index because that is what Meta substitutes — the
                          template body carries no parameter names. */}
                      {Array.from({
                        length: selectedTemplate.variableCount,
                      }).map((_, i) => (
                        <Input
                          key={i}
                          className="h-8 text-xs bg-white"
                          value={currentVars[i] ?? ""}
                          placeholder={`{{${i + 1}}}`}
                          onChange={(e) =>
                            setVarAt(
                              templateKey(selectedTemplate),
                              i,
                              e.target.value
                            )
                          }
                        />
                      ))}

                      {/* What the tenant will actually receive, once the
                          variables are filled in. Suppressed for a
                          zero-variable template, where it would only repeat
                          the card verbatim. */}
                      {selectedTemplate.variableCount > 0 ? (
                        <p className="whitespace-pre-wrap rounded-lg bg-white/70 px-2 py-1.5 text-xs text-amber-900">
                          {templatePreview}
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  <Button
                    type="button"
                    size="sm"
                    className="h-7 rounded-full text-xs bg-[#35CFC8] hover:bg-[#2fbdb7] text-white"
                    disabled={
                      isSendInFlight ||
                      !currentUserId ||
                      !selectedTemplate ||
                      !varsComplete
                    }
                    onClick={() =>
                      selectedTemplate &&
                      postComment.mutate({
                        sendAsTemplate: true,
                        template: selectedTemplate,
                        templateVariables: currentVars.map((v) => v.trim()),
                        renderedBody: templatePreview,
                      })
                    }
                  >
                    {isSendInFlight ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      t(
                        "issueChatThread.sendWhatsappTemplate",
                        "Send WhatsApp notification"
                      )
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {canPostInternal && showInternalToggle && (
            <div className="flex items-center gap-2 pb-2">
              <Button
                type="button"
                variant={postInternal ? "default" : "outline"}
                size="sm"
                className={cn(
                  "rounded-full h-7 text-xs",
                  postInternal &&
                    "bg-amber-100 text-amber-900 hover:bg-amber-200 border border-amber-200"
                )}
                onClick={() => setPostInternal((v) => !v)}
              >
                {postInternal
                  ? t("issueChatThread.internalNote", "Internal note")
                  : t("issueChatThread.addInternalNote", "+ Internal note")}
              </Button>
              <span className="text-[11px] text-muted-foreground">
                {t("issueChatThread.hiddenFromTenants", "Hidden from tenants.")}
              </span>
            </div>
          )}

          {/* Task 9 — the free-text box (attach/emoji/textarea/send), and the
              keyboard hint below it, only render while a free-form WhatsApp
              reply is actually deliverable (any non-WhatsApp channel, or a
              WhatsApp ticket whose service window is still open). Once
              closed, the block above is the only way to reply. */}
          {supportAppearance && canPostInternal && whatsappWindowClosed && (
            <button
              type="button"
              className="support-note-toggle"
              aria-pressed={postInternal}
              disabled={isSendInFlight}
              onClick={() => {
                setPostInternal((value) => !value);
                setAssistOpen(false);
              }}
            >
              {postInternal
                ? t("support.composer.writeReply", "Write a reply")
                : t("issueChatThread.internalNote", "Internal note")}
            </button>
          )}

          {!whatsappWindowClosed && (
            <>
              {supportAppearance && !postInternal && canUseAiAssist && (
                <InlineDraftControl
                  issueId={issueId}
                  draft={message}
                  onDraft={setMessage}
                  actionContainer={draftActionTarget}
                  disabled={isSendInFlight}
                />
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx"
                className="hidden"
                onChange={(e) => {
                  const allowed = new Set([
                    "application/pdf",
                    "image/png",
                    "image/jpeg",
                    "image/gif",
                    "image/webp",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  ]);
                  const picked = Array.from(e.target.files || []);
                  const channelAllowed =
                    channel === "whatsapp"
                      ? new Set([
                          "application/pdf",
                          "image/png",
                          "image/jpeg",
                          "application/msword",
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                        ])
                      : allowed;
                  const available =
                    channel === "whatsapp"
                      ? Math.max(1 - chatFiles.length, 0)
                      : picked.length;
                  const newFiles = picked
                    .filter((file) => {
                      const limit =
                        channel === "whatsapp" && file.type.startsWith("image/")
                          ? 5 * 1024 * 1024
                          : 10 * 1024 * 1024;
                      return (
                        file.size <= limit && channelAllowed.has(file.type)
                      );
                    })
                    .slice(0, available);
                  if (newFiles.length !== picked.length) {
                    toast.error(
                      t(
                        "support.attachments.invalid",
                        channel === "whatsapp"
                          ? "WhatsApp accepts one PDF, Word file, JPEG or PNG; images may be up to 5 MB and documents up to 10 MB."
                          : "Files must be an allowed type and no larger than 10 MB."
                      )
                    );
                  }
                  setChatFiles((prev) => [...prev, ...newFiles]);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              />

              <div
                className={
                  supportAppearance
                    ? "support-compose-row relative"
                    : "relative flex items-end gap-2"
                }
              >
                <div className="flex-1 flex flex-col gap-1">
                  {chatFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 px-2 pt-1.5">
                      {chatFiles.map((f, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1 rounded-full bg-[#35CFC8]/15 border border-[#35CFC8]/30 px-2.5 py-1 text-xs text-[#0f766e] max-w-[160px]"
                        >
                          {f.type.startsWith("image/") ? (
                            <ImageIcon className="h-3 w-3 shrink-0" />
                          ) : (
                            <FileText className="h-3 w-3 shrink-0" />
                          )}
                          <span className="truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setChatFiles((prev) =>
                                prev.filter((_, j) => j !== i)
                              )
                            }
                            className="ml-0.5 shrink-0 rounded-full hover:bg-[#35CFC8]/30 p-0.5"
                            aria-label={t(
                              "issueChatThread.removeFile",
                              "Remove {name}"
                            ).replace("{name}", f.name)}
                          >
                            <XIcon className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    className={
                      supportAppearance
                        ? "support-composer-box"
                        : "flex items-end gap-1 rounded-3xl bg-background border px-2 py-1.5 transition-shadow border-[#35CFC8]/60 shadow-[0_0_0_3px_rgba(53,207,200,0.10)]"
                    }
                  >
                    {supportAppearance && (
                      <span
                        className="support-ai-action-slot"
                        ref={setDraftActionTarget}
                      />
                    )}
                    {
                      <Button
                        ref={emojiButtonRef}
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("issueChatThread.addEmoji", "Add emoji")}
                        aria-expanded={emojiOpen}
                        className={cn(
                          "h-9 w-9 shrink-0 rounded-full transition-colors",
                          emojiOpen
                            ? "bg-[#35CFC8]/15 text-[#0f766e]"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEmojiOpen((v) => !v);
                        }}
                      >
                        <Smile className="h-5 w-5" />
                      </Button>
                    }

                    {supportAppearance && canPostInternal && (
                      <>
                        <button
                          type="button"
                          className="support-note-toggle"
                          aria-pressed={postInternal}
                          disabled={isSendInFlight}
                          onClick={() => {
                            setPostInternal((value) => !value);
                            setAssistOpen(false);
                          }}
                        >
                          {postInternal
                            ? t("support.composer.backToReply", "Back to reply")
                            : t(
                                "issueChatThread.internalNote",
                                "Internal note"
                              )}
                        </button>
                      </>
                    )}

                    {canUseAiAssist && !supportAppearance && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t(
                          "issueChatThread.assistOpen",
                          "coliviq Agent"
                        )}
                        aria-expanded={assistOpen}
                        aria-controls="coliviq-agent-panel"
                        className={cn(
                          "h-9 w-9 shrink-0 rounded-full transition-colors",
                          assistOpen
                            ? "bg-[#35CFC8]/15 text-[#0f766e]"
                            : "text-muted-foreground hover:bg-[#35CFC8]/10 hover:text-[#0f766e]"
                        )}
                        onClick={() => {
                          setEmojiOpen(false);
                          // Review finding: this toggle can also close the
                          // panel (click while open), same as the panel's
                          // own X button — which already resets edit mode.
                          // Without resetting it here too, a reopen that
                          // regenerates a fresh assistReplyText (because a
                          // new message arrived — see assistGeneratedFor
                          // CommentIdRef) would still show the OLD
                          // assistEditDraft from before the close, and Save
                          // would overwrite the fresh reply with stale text.
                          if (assistOpen) setAssistIsEditing(false);
                          setAssistOpen((open) => !open);
                        }}
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                    )}

                    {supportAppearance && !postInternal && (
                      <span className="support-via">
                        {t("support.composer.via", "via")}{" "}
                        <strong>
                          {channel === "whatsapp"
                            ? "WhatsApp"
                            : t("support.channel.email", "Email")}
                        </strong>
                      </span>
                    )}
                    {supportAppearance &&
                      !postInternal &&
                      language === "en" &&
                      translatedFrom && (
                        <span className="support-send-language">
                          {t(
                            "issueChatThread.willSendEnglish",
                            "Will be sent in English"
                          )}
                        </span>
                      )}
                    {supportAppearance &&
                      !internalOnly &&
                      !(supportAppearance && postInternal) &&
                      channel === "email" && (
                        <div className="support-send-as flex items-center gap-1">
                          <span className="sr-only">
                            {t("issueChatThread.sendAsLabel", "Send as")}
                          </span>
                          <Select
                            value={fromAddress ?? SEND_AS_IDENTITIES[0]}
                            onValueChange={(v) =>
                              setFromAddress(v as SendAsIdentity)
                            }
                          >
                            <SelectTrigger
                              className="h-7 w-auto max-w-[170px] border-0 shadow-none text-xs"
                              aria-label={t(
                                "issueChatThread.sendAsLabel",
                                "Send as"
                              )}
                            >
                              <span>
                                {t("support.composer.from", "from")}{" "}
                                {
                                  (fromAddress ?? SEND_AS_IDENTITIES[0]).split(
                                    "@"
                                  )[0]
                                }
                                @
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {SEND_AS_IDENTITIES.map((addr) => (
                                <SelectItem
                                  key={addr}
                                  value={addr}
                                  className="text-xs"
                                >
                                  {addr}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    {supportAppearance &&
                    !postInternal &&
                    channel === "email" ? (
                      <details className="support-signature-preview">
                        <summary>
                          {t("support.composer.signature", "Signature")}
                        </summary>
                        <span>
                          {
                            "--\ncoliviq team\n\ncoliviq GmbH\nElvirastraße 10\n80636 München\n\ne-mail: kontakt@coliviq.de\nweb: www.coliviq.de"
                          }
                        </span>
                      </details>
                    ) : null}

                    {supportAppearance &&
                    postInternal &&
                    mentionRoster.length ? (
                      <div
                        className="support-mention-chips"
                        aria-label={t(
                          "support.notes.mention",
                          "Mention teammate"
                        )}
                      >
                        {mentionRoster.map((person) => {
                          const name = adminDisplayName(person, t).split(
                            " "
                          )[0];
                          const selected = mentionIds.includes(person.id);
                          return (
                            <button
                              key={person.id}
                              type="button"
                              aria-pressed={selected}
                              aria-label={
                                selected
                                  ? t(
                                      "support.notes.removeMention",
                                      "Remove mention {name}"
                                    ).replace("{name}", name)
                                  : t(
                                      "support.notes.addMention",
                                      "Mention {name}"
                                    ).replace("{name}", name)
                              }
                              onClick={() =>
                                setMentionIds((current) =>
                                  current.includes(person.id)
                                    ? current.filter((id) => id !== person.id)
                                    : [...current, person.id]
                                )
                              }
                            >
                              @{name}
                              {selected ? " ×" : ""}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    <Textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={
                        supportAppearance && postInternal
                          ? internalNotePlaceholder
                          : composerPlaceholder
                      }
                      aria-label={composerLabel}
                      rows={supportAppearance ? 3 : 1}
                      // Unlike the old design, coliviq Agent's draft lives in
                      // its own panel state (assistReplyText), never written
                      // into this textarea until the admin explicitly uses
                      // it — so generation in the background can no longer
                      // clobber what's typed here, and the normal composer
                      // must stay fully usable while the panel is open.
                      className={cn(
                        "flex-1 min-h-[44px] max-h-[200px] resize-none overflow-y-auto",
                        "border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0",
                        "px-1 py-2 text-sm leading-relaxed shadow-none"
                      )}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                      spellCheck={false}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          (!supportAppearance || e.ctrlKey || e.metaKey) &&
                          !e.nativeEvent.isComposing &&
                          (message.trim() || chatFiles.length) &&
                          currentUserId &&
                          !postComment.isPending &&
                          !uploadingFiles
                        ) {
                          e.preventDefault();
                          postComment.mutate({});
                        }
                      }}
                    />

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t(
                        "issueChatThread.attachFiles",
                        "Attach files"
                      )}
                      className={cn(
                        "h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-[#0f766e] hover:bg-[#35CFC8]/10 transition-colors",
                        supportAppearance && "support-attach-files"
                      )}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* While a send is in flight the button must still read as the
                send button doing something. It used to fall into the plain
                `disabled:bg-muted` state and — with the textarea cleared —
                looked like it had simply disappeared, leaving no way to tell
                a send in progress from a send that never started. Keep the
                brand fill, swap in a spinner, and mark it busy for AT. */}
                <Button
                  type="button"
                  aria-label={sendLabel}
                  aria-busy={isSendInFlight}
                  className={cn(
                    "h-11 w-11 shrink-0 rounded-full bg-[#35CFC8] hover:bg-[#2fbdb7] text-white shadow-md transition-all",
                    supportAppearance && "support-send",
                    "disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none",
                    isSendInFlight &&
                      "disabled:bg-[#35CFC8]/70 disabled:text-white disabled:shadow-md",
                    (message.trim() || chatFiles.length) &&
                      !isSendInFlight &&
                      "active:scale-95"
                  )}
                  disabled={
                    isSendInFlight ||
                    (!message.trim() && !chatFiles.length) ||
                    !currentUserId
                  }
                  onClick={() => postComment.mutate({})}
                >
                  {isSendInFlight ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : supportAppearance ? (
                    t("common.send", "Send")
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </Button>

                {emojiOpen && (
                  <div
                    ref={emojiPanelRef}
                    role="dialog"
                    aria-label={t(
                      "issueChatThread.emojiPicker",
                      "Emoji picker"
                    )}
                    className="absolute left-0 z-[60] overflow-hidden rounded-2xl border border-border/60 bg-background shadow-2xl animate-in fade-in-0 zoom-in-95 duration-150"
                    style={{ bottom: "calc(100% + 10px)" }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EmojiPicker
                      theme={Theme.LIGHT}
                      searchDisabled={false}
                      lazyLoadEmojis
                      height={380}
                      width={340}
                      previewConfig={{ showPreview: false }}
                      skinTonesDisabled
                      onEmojiClick={(
                        emojiData: EmojiClickData,
                        maybeEvent?: unknown
                      ) => {
                        const emoji =
                          (emojiData as unknown as { emoji?: string } | null)
                            ?.emoji ??
                          (maybeEvent as unknown as { emoji?: string } | null)
                            ?.emoji;
                        if (!emoji) return;
                        insertEmoji(emoji);
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                className={
                  supportAppearance
                    ? "hidden"
                    : "mt-1 px-1 text-[10px] text-muted-foreground/70"
                }
              >
                {t("issueChatThread.pressPrefix", "Press")}{" "}
                <kbd className="px-1 py-0.5 rounded bg-muted/60 font-medium">
                  {t("issueChatThread.kbdEnter", "Enter")}
                </kbd>{" "}
                {t("issueChatThread.toSend", "to send,")}{" "}
                <kbd className="px-1 py-0.5 rounded bg-muted/60 font-medium">
                  {t("issueChatThread.kbdShift", "Shift")}
                </kbd>
                +
                <kbd className="px-1 py-0.5 rounded bg-muted/60 font-medium">
                  {t("issueChatThread.kbdEnter", "Enter")}
                </kbd>{" "}
                {t("issueChatThread.forNewLine", "for a new line")}
              </div>
            </>
          )}
        </div>
      )}

      {/* COL-322 — delete confirmation (soft delete, tombstone stays) */}
      <AlertDialog
        open={!!deletingCommentId}
        onOpenChange={(open) => {
          if (!open) setDeletingCommentId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("issueChatThread.deleteMessageTitle", "Delete this message?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "issueChatThread.deleteMessageDescription",
                "The message will be replaced with a \u201CMessage deleted\u201D placeholder for everyone in this conversation. This can\u2019t be undone."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("issueChatThread.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteComment.isPending}
              onClick={() => {
                if (deletingCommentId) deleteComment.mutate(deletingCommentId);
              }}
            >
              {t("issueChatThread.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
